import { useState, useEffect, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Loader2, AlertCircle, ChevronLeft, Users,
  Package, Coffee, Plus, Pencil, Trash2, CheckCircle2, X,
  Award, Globe, QrCode, ChevronDown, Upload, FileText, Download, Printer, Receipt, Mail,
  Search, SlidersHorizontal, RotateCcw, GraduationCap, Paperclip, Eye, Lock,
} from "lucide-react";
import { BASE_URL } from "../../../features/api/apiSlice";
import {
  useAdminGetEventQuery,
  useAdminCompleteEventMutation,
  useAdminAllocateCpdMutation,
  useAdminGetEventRegistrationsQuery,
  useAdminGetEventReportQuery,
  useAdminGetMaterialsQuery,
  useAdminCreateMaterialMutation,
  useAdminUpdateMaterialMutation,
  useAdminDeleteMaterialMutation,
  useAdminGetEventDocumentsQuery,
  useAdminUploadEventDocumentMutation,
  useAdminUpdateEventDocumentMutation,
  useAdminDeleteEventDocumentMutation,
  useAdminGetRefreshmentsQuery,
  useAdminCreateRefreshmentMutation,
  useAdminUpdateRefreshmentMutation,
  useAdminDeleteRefreshmentMutation,
  useLazyScannerLookupQuery,
  useScannerMarkAttendanceMutation,
  useScannerMarkRefreshmentMutation,
  useScannerUpdateMaterialsMutation,
  useAdminAddMaterialStockMutation,
  useAdminIssueRegistrationMaterialsMutation,
  useLazyAdminSearchEventMembersQuery,
  useAdminCreateRegistrationMutation,
  useAdminPreviewRegistrationImportMutation,
  useAdminImportRegistrationsMutation,
  useAdminRecordRegistrationPaymentMutation,
  useAdminGetRegistrationAccessCardQuery,
  useAdminGetEventGuestsQuery,
  useAdminCreateEventGuestMutation,
  useAdminDeleteEventGuestMutation,
  useAdminGetMissingReceiptsQuery,
  useAdminBackfillEventReceiptsMutation,
  eventReportQueryString,
} from "../api/adminEventsApi";
import type { EventReportArgs } from "../api/adminEventsApi";
import { AccessCardView } from "../components/AccessCard";
import { useGetLedgerAccountsQuery, useResendPaymentReceiptMutation } from "../../billing/api/billingApi";
import { Link } from "react-router-dom";

import AccessGate from "../../../components/AccessGate";
import PageGate from "../../../components/PageGate";
import { PERMS } from "../../../utils/perms";
const naira = (n: any) => `₦${Number(n || 0).toLocaleString("en-NG")}`;

/** The three fees on an event's timeline, as the API names them. */
const WINDOW_LABELS: Record<string, string> = {
  early_bird: "Early bird",
  normal:     "Normal",
  late:       "Late",
};

function extractError(err: any): string {
  if (err?.data?.errors) {
    const first = Object.values(err.data.errors)[0];
    return Array.isArray(first) ? (first[0] as string) : String(first);
  }
  return err?.data?.message ?? err?.data?.error ?? "An error occurred";
}
function fmt(d: string) {
  return new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
}

const STATUS_COLORS: Record<string, string> = {
  published: "bg-emerald-50 text-emerald-700 border-emerald-100",
  draft:     "bg-amber-50 text-amber-700 border-amber-100",
  cancelled: "bg-rose-50 text-rose-700 border-rose-100",
  completed: "bg-gray-50 text-gray-500 border-gray-100",
};

/** Everything but letters and digits, so "F/06970" and "f 06970" compare equal. */
const squash = (v: any) => String(v ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Does this registration match what the desk typed?
 *
 * The desk is handed a membership card, a printed slip or an email address —
 * whichever the delegate has on them — so all three have to find the row.
 *
 * Name and email are checked on BOTH the registration and the linked account:
 * a member registered by an admin carries the typed attendee_name while their
 * account holds their real one, and the two are frequently not the same string.
 * The earlier `attendee_name ?? user.name` searched only whichever existed
 * first, so a delegate entered at the desk could not be found by their account
 * name — and their membership number was not searched at all.
 *
 * Membership numbers are additionally compared with punctuation stripped,
 * because nobody types them the same way twice.
 */
function registrationMatches(r: any, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const member = r.user?.member;

  const fields = [
    r.attendee_name,
    r.user?.name,
    member?.surname,
    member?.firstname,
    member?.othernames,
    r.registration_no,
    r.attendee_email,
    r.user?.email,
    r.attendee_phone,
    member?.membership_no,
  ];

  if (fields.some((f) => String(f ?? "").toLowerCase().includes(q))) return true;

  // Membership number typed in any of its written forms.
  const squashedQuery = squash(q);
  return !!squashedQuery && squash(member?.membership_no).includes(squashedQuery);
}

// "materials" is the physical stock table; "documents" is the file library.
// Two tabs, not one — see DocumentsPanel.
type Tab = "registrations" | "documents" | "materials" | "refreshments" | "report";

// ── Registrant expanded row ───────────────────────────────────────────────────
function RegistrantRow({
  eventId, reg, days, eventMaterials, eventRefreshments, onChanged,
}: {
  eventId: string;
  reg: any;
  days: any[];
  eventMaterials: any[];
  eventRefreshments: any[];
  /** Refetch the registrations list — the row's own actions change it. */
  onChanged?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [subTab, setSubTab]     = useState<"attendance" | "food" | "materials">("attendance");
  const [busyKey, setBusyKey]   = useState("");
  const [busyMatId, setBusyMatId] = useState<number | null>(null);
  const [rowMsg, setRowMsg]     = useState<{ ok: boolean; text: string } | null>(null);
  const [payOpen, setPayOpen]   = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [resendReceipt, { isLoading: resending }] = useResendPaymentReceiptMutation();
  const [backfillReceipt, { isLoading: creatingReceipt }] = useAdminBackfillEventReceiptsMutation();

  /**
   * Raise this one registration's missing billing records.
   *
   * The server refuses rather than guesses when the registration names no bank
   * account — its reason is reported as-is, and the banner above the list is
   * where an account can be chosen for those.
   */
  const createReceipt = async () => {
    try {
      const res: any = await backfillReceipt({ eventId, registration_id: reg.id }).unwrap();
      const skipped = res?.data?.skipped?.[0]?.reason;
      setRowMsg(
        res?.data?.created
          ? { ok: true, text: res.message }
          : { ok: false, text: skipped ?? res?.message ?? "Could not create the receipt." },
      );
      onChanged?.();
    } catch (e: any) {
      setRowMsg({ ok: false, text: extractError(e) });
    }
  };

  // Settlement already emails the member their receipt (and its PDF). This
  // re-sends it — the copies queued while the queue worker was down are gone,
  // and nothing retries them.
  const emailReceipt = async () => {
    if (!reg.payment_id) return;
    try {
      const res: any = await resendReceipt(reg.payment_id).unwrap();
      setRowMsg({ ok: true, text: res?.message ?? "Receipt queued." });
    } catch (e: any) {
      setRowMsg({ ok: false, text: extractError(e) });
    }
  };

  const [triggerLookup, { data: detail, isLoading: detailLoading, isFetching }] = useLazyScannerLookupQuery();
  const [markAttendance]  = useScannerMarkAttendanceMutation();
  const [markRefreshment] = useScannerMarkRefreshmentMutation();
  const [updateMaterials] = useScannerUpdateMaterialsMutation();

  const notify = (ok: boolean, text: string) => {
    setRowMsg({ ok, text });
    setTimeout(() => setRowMsg(null), 3000);
  };

  useEffect(() => {
    if (expanded && reg.barcode_data) triggerLookup(reg.barcode_data);
  }, [expanded]);

  // Re-read this registrant, and tell the list above to re-read too.
  //
  // No `preferCacheValue` on the trigger: it used to pass `true`, so a reload
  // after an action returned the payload cached from before it and the row
  // showed the old state until the page was refreshed by hand.
  //
  // onChanged refreshes the surrounding registrations list, whose collapsed
  // rows carry the attendance and payment chips this action just changed.
  const reload = () => {
    if (reg.barcode_data) triggerLookup(reg.barcode_data);
    onChanged?.();
  };

  const handleAttendance = async (dayId: number) => {
    if (!reg.barcode_data) return;
    setBusyKey(`att-${dayId}`);
    try {
      await markAttendance({ barcode: reg.barcode_data, event_day_id: dayId }).unwrap();
      reload();
      notify(true, "Attendance marked");
    } catch { notify(false, "Failed to mark attendance"); }
    setBusyKey("");
  };

  const handleRefreshment = async (dayId: number) => {
    if (!reg.barcode_data) return;
    setBusyKey(`ref-${dayId}`);
    try {
      await markRefreshment({ barcode: reg.barcode_data, event_day_id: dayId }).unwrap();
      reload();
      notify(true, "Food issued");
    } catch { notify(false, "Failed to issue food"); }
    setBusyKey("");
  };

  const handleIssueMaterial = async (eventMaterialId: number) => {
    if (!reg.barcode_data) return;
    setBusyMatId(eventMaterialId);
    try {
      await updateMaterials({
        registrationId: reg.id,
        material_updates: [{ event_material_id: eventMaterialId, is_collected: true }],
      }).unwrap();
      reload();
      notify(true, "Material issued");
    } catch { notify(false, "Failed to issue material"); }
    setBusyMatId(null);
  };

  // Lookup maps built from scanner detail
  const attMap: Record<number, boolean> = {};
  (detail?.attendance ?? []).forEach((a: any) => {
    attMap[a.event_day_id ?? a.day_id] = !!a.is_present;
  });

  // refreshment_choices is now per-day: { event_day_id, day_label, chosen_name, is_collected, available_options }
  const refByDay: Record<number, any> = {};
  (detail?.refreshment_choices ?? []).forEach((c: any) => { refByDay[c.event_day_id] = c; });

  // Materials collection state from scanner detail (event master list with overlay)
  const matCollectedMap: Record<number, boolean> = {};
  (detail?.materials ?? []).forEach((m: any) => { matCollectedMap[m.id] = !!m.is_collected; });

  const noBarcode = !reg.barcode_data;

  /**
   * The desk's per-row controls.
   *
   * Rendered twice: inline beside the name from `sm` up, and on their own
   * wrapped row underneath on a phone, where four labelled buttons plus a name
   * do not share a line. Held in a variable rather than duplicated so the two
   * cannot drift apart.
   */
  const actions = (
    <>
      {/* Reprint from the desk. Only where there is a card to print: the
          fee is settled and the registration actually carries a barcode. */}
      {(reg.payment_status === "paid" || reg.payment_status === "free") && reg.barcode_data && (
        <button
          onClick={(e) => { e.stopPropagation(); setCardOpen(true); }}
          title="View / print this attendee's access card"
          className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest px-2 py-1.5 sm:py-1 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-100 hover:bg-indigo-100"
        >
          <QrCode className="w-3 h-3" /> Card
        </button>
      )}
      {/* Taking money on the Institute's behalf: the capability AND an admin
          role, matching what the API now enforces on this endpoint. Offering a
          button that is going to come back 403 is worse than not offering it. */}
      {reg.payment_status === "pending" && (
        <AccessGate perm={PERMS.event.registrationManage} roles={["admin", "super_admin"]}>
          <button
            onClick={(e) => { e.stopPropagation(); setPayOpen(true); }}
            className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest px-2 py-1.5 sm:py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
          >
            <Upload className="w-3 h-3" /> Record payment
          </button>
        </AccessGate>
      )}
      {/* The same document the member was emailed, opened from the desk.
          `receipt_ref` is null when no Payment row exists — a free
          registration, or a non-member attendee who cannot be invoiced —
          so the link only appears where there is something to show. */}
      {reg.payment_status === "paid" && reg.receipt_ref && (
        <Link
          to={`/receipt/${reg.receipt_ref}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest px-2 py-1.5 sm:py-1 rounded-lg bg-gray-900 text-white hover:bg-emerald-600"
        >
          <Receipt className="w-3 h-3" /> Receipt
        </Link>
      )}
      {/* Settled, but never billed — a guest fee taken before payments could
          belong to a non-member. One click raises the invoice, the receipt and
          the ledger entry from what the registration already records. */}
      {reg.payment_status === "paid" && !reg.payment_id && (
        <button
          onClick={(e) => { e.stopPropagation(); createReceipt(); }}
          disabled={creatingReceipt}
          title="Create the missing receipt and post this to the ledger"
          className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest px-2 py-1.5 sm:py-1 rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
        >
          {creatingReceipt ? <Loader2 className="w-3 h-3 animate-spin" /> : <Receipt className="w-3 h-3" />}
          Create receipt
        </button>
      )}
      {reg.payment_status === "paid" && reg.payment_id && (
        <button
          onClick={(e) => { e.stopPropagation(); emailReceipt(); }}
          disabled={resending}
          title="Email this receipt to the member again"
          className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest px-2 py-1.5 sm:py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-100 hover:bg-emerald-100 disabled:opacity-50"
        >
          {resending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
          Email
        </button>
      )}
    </>
  );

  const statusChips = (
    <>
      <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg border ${
        reg.overall_attendance === "present" ? "bg-emerald-50 text-emerald-700 border-emerald-100" :
        reg.overall_attendance === "partial"  ? "bg-amber-50 text-amber-700 border-amber-100" :
        "bg-gray-100 text-gray-400 border-gray-100"
      }`}>{reg.overall_attendance ?? "absent"}</span>
      <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg border ${
        reg.payment_status === "paid" || reg.payment_status === "free"
          ? "bg-emerald-50 text-emerald-700 border-emerald-100"
          : "bg-amber-50 text-amber-700 border-amber-100"
      }`}>{reg.payment_status}</span>
    </>
  );

  return (
    <div className="rounded-2xl overflow-hidden border border-gray-100">
      {/* Row header */}
      <div
        className="p-3.5 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors select-none"
        onClick={() => setExpanded((p) => !p)}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="font-bold text-gray-800 text-sm truncate">{reg.attendee_name ?? reg.user?.name ?? "—"}</div>
            <div className="text-[10px] text-gray-400 font-bold mt-0.5 break-words">
              {reg.registration_no}
              {/* Shown because it is now searchable: someone who found this row
                  by typing a membership number needs to see the number they
                  typed to know they have the right delegate. */}
              {reg.user?.member?.membership_no && (
                <span className="text-gray-500">{" · "}{reg.user.member.membership_no}</span>
              )}
              {/* Who took the money, named. "Recorded by admin" is only half an
                  answer during a reconciliation — the question that follows is
                  always which admin. */}
              {reg.source === "admin" && (
                <span className="text-sky-600">
                  {" · "}
                  {reg.recorded_by_name ? `Recorded by ${reg.recorded_by_name}` : "Recorded by admin"}
                  {reg.payment_method ? ` (${String(reg.payment_method).replace(/_/g, " ")})` : ""}
                </span>
              )}
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
            {actions}
            {statusChips}
          </div>
          <ChevronDown className={`w-4 h-4 shrink-0 text-gray-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
        </div>

        {/* Phone layout: chips and controls on their own wrapped row. */}
        <div className="sm:hidden flex flex-wrap items-center gap-1.5 mt-2.5">
          {statusChips}
          {actions}
        </div>
      </div>

      {/* Outside the expanded section on purpose: Email, Create receipt and the
          card all live in the collapsed header, and their outcome was being
          written somewhere only an expanded row could show. */}
      {rowMsg && (
        <div className={`text-xs font-bold px-3.5 py-2 border-t ${
          rowMsg.ok
            ? "bg-emerald-50 text-emerald-700 border-emerald-100"
            : "bg-rose-50 text-rose-700 border-rose-100"
        }`}>
          {rowMsg.text}
        </div>
      )}

      {payOpen && (
        <RecordPaymentModal eventId={eventId} reg={reg} onClose={() => setPayOpen(false)} />
      )}

      {cardOpen && (
        <AccessCardModal eventId={eventId} reg={reg} onClose={() => setCardOpen(false)} />
      )}

      {/* Expanded section */}
      {expanded && (
        <div className="bg-white border-t border-gray-100">
          {/* Sub-tabs */}
          <div className="flex border-b border-gray-50">
            {([
              { key: "attendance", label: "Attendance" },
              { key: "food",       label: "Food" },
              { key: "materials",  label: "Materials" },
            ] as const).map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setSubTab(key)}
                className={`flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest transition-colors ${
                  subTab === key ? "border-b-2 border-indigo-500 text-indigo-600" : "text-gray-400 hover:text-gray-600"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="p-4 space-y-3">
            {noBarcode && (
              <p className="text-[11px] font-medium text-amber-600 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                No QR code found for this registration — use the Scanner page instead.
              </p>
            )}

            {(detailLoading || isFetching) && (
              <div className="flex justify-center py-3">
                <Loader2 className="w-4 h-4 text-indigo-400 animate-spin" />
              </div>
            )}

            {/* Attendance sub-tab */}
            {subTab === "attendance" && !detailLoading && (
              <div className="space-y-1.5">
                {days.length === 0 && (
                  <p className="text-xs text-gray-400 font-medium text-center py-2">No event days configured.</p>
                )}
                {days.map((day: any) => {
                  const isPresent = !!attMap[day.id];
                  const busy = busyKey === `att-${day.id}`;
                  return (
                    <div key={day.id} className={`flex items-center justify-between gap-3 p-2.5 rounded-xl border transition-colors ${isPresent ? "bg-emerald-50 border-emerald-100" : "bg-gray-50 border-transparent"}`}>
                      <div className="min-w-0">
                        <div className={`text-xs font-bold ${isPresent ? "text-emerald-800" : "text-gray-700"}`}>{day.day_label}</div>
                        <div className="text-[10px] text-gray-400 font-medium">{day.day_date ? fmt(day.day_date) : ""}</div>
                      </div>
                      {isPresent ? (
                        <span className="text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg bg-emerald-100 text-emerald-700 border border-emerald-200 flex items-center gap-1 shrink-0">
                          <CheckCircle2 className="w-3 h-3" /> Present
                        </span>
                      ) : (
                        <button
                          onClick={() => handleAttendance(day.id)}
                          disabled={busy || noBarcode}
                          className="text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-40 transition-colors flex items-center gap-1 shrink-0"
                        >
                          {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                          Mark Present
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Food sub-tab */}
            {subTab === "food" && !detailLoading && (
              <div className="space-y-1.5">
                {Object.keys(refByDay).length === 0 && (
                  <p className="text-xs text-gray-400 font-medium text-center py-2">No refreshments configured for this event.</p>
                )}
                {days.map((day: any) => {
                  const dayRef = refByDay[day.id];
                  if (!dayRef) return null;
                  const collected = !!dayRef.is_collected;
                  const busy = busyKey === `ref-${day.id}`;
                  return (
                    <div key={day.id} className={`flex items-center justify-between p-2.5 rounded-xl border transition-colors ${collected ? "bg-emerald-50 border-emerald-100" : "bg-gray-50 border-transparent"}`}>
                      <div className="flex-1 min-w-0 mr-3">
                        <div className={`text-xs font-bold ${collected ? "text-emerald-800" : "text-gray-700"}`}>{day.day_label}</div>
                        {dayRef.chosen_name ? (
                          <div className="text-[10px] font-bold text-indigo-600 truncate">Choice: {dayRef.chosen_name}</div>
                        ) : (
                          <div className="text-[10px] text-gray-400 font-medium truncate">
                            {(dayRef.available_options ?? []).map((o: any) => o.name).join(", ")}
                          </div>
                        )}
                        {collected && dayRef.collected_at && (
                          <div className="text-[10px] text-emerald-600 font-bold">Issued {fmt(dayRef.collected_at)}</div>
                        )}
                      </div>
                      {collected ? (
                        <span className="text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg bg-emerald-100 text-emerald-700 border border-emerald-200 flex items-center gap-1 shrink-0">
                          <CheckCircle2 className="w-3 h-3" /> Issued
                        </span>
                      ) : (
                        <button
                          onClick={() => handleRefreshment(day.id)}
                          disabled={busy || noBarcode}
                          className="text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 transition-colors shrink-0 flex items-center gap-1"
                        >
                          {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                          Issue Food
                        </button>
                      )}
                    </div>
                  );
                })}
                {false && (
                  <div className="p-2.5 bg-gray-50 rounded-xl">
                    <div className="text-xs font-bold text-gray-700 mb-0.5">All Days</div>
                    <div className="text-[10px] text-gray-400 font-medium">
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Materials sub-tab */}
            {subTab === "materials" && !detailLoading && (
              <div className="space-y-1.5">
                {eventMaterials.length === 0 && (
                  <p className="text-xs text-gray-400 font-medium text-center py-2">No materials configured for this event.</p>
                )}
                {eventMaterials.map((m: any) => {
                  const isIssued = !!matCollectedMap[m.id];
                  const busy = busyMatId === m.id;
                  return (
                    <div key={m.id} className={`flex items-center justify-between p-2.5 rounded-xl border transition-colors ${isIssued ? "bg-emerald-50 border-emerald-100" : "bg-gray-50 border-transparent"}`}>
                      <div className="flex-1 min-w-0 mr-3">
                        <div className={`text-xs font-bold flex items-center gap-1.5 ${isIssued ? "text-emerald-800" : "text-gray-700"}`}>
                          {isIssued && <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />}
                          {m.name}
                        </div>
                        {m.description && <div className="text-[10px] text-gray-400">{m.description}</div>}
                      </div>
                      {isIssued ? (
                        <span className="text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg bg-emerald-100 text-emerald-700 border border-emerald-200 shrink-0">
                          Issued ✓
                        </span>
                      ) : (
                        <button
                          onClick={() => handleIssueMaterial(m.id)}
                          disabled={busy || noBarcode}
                          className="text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40 transition-colors shrink-0 flex items-center gap-1"
                        >
                          {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                          Issue
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Access Card (admin view / reprint) ────────────────────────────────────────
// The attendee who turns up without their card, and the guest or admin-entered
// registrant who has no portal login to print one from, both end up at the
// desk. This is the same card the member sees — fetched from the API rather
// than assembled from the row, because the row carries no photograph.
function AccessCardModal({ eventId, reg, onClose }: { eventId: string; reg: any; onClose: () => void }) {
  const { data: card, isLoading, isError, error } = useAdminGetRegistrationAccessCardQuery({
    eventId,
    registrationId: reg.id,
  });

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-gray-900">Access Card</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-500" /></button>
        </div>

        <div className="p-4 sm:p-6">
          {isLoading && (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-indigo-400 animate-spin" /></div>
          )}

          {isError && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2 font-medium">
              {extractError(error)}
            </div>
          )}

          {card && <AccessCardView card={card} />}
        </div>
      </div>
    </div>
  );
}

/**
 * Settled registrations on this event that were never billed, and the button
 * that fixes them.
 *
 * Until payments could belong to somebody without an account, a non-member who
 * paid at the desk was marked paid on their registration and recorded nowhere
 * else — no invoice, no receipt to hand them, and nothing in finance analysis
 * to reconcile against the bank. The money is real and the registration still
 * holds the reference, the method and the account, so the paperwork can be
 * raised from what is already there.
 *
 * Renders nothing when there is nothing outstanding, which is the normal state
 * of every event from here on.
 */
function MissingReceiptsBanner({ eventId }: { eventId: string }) {
  const { data: missing } = useAdminGetMissingReceiptsQuery(eventId);
  const [backfill, { isLoading: running }] = useAdminBackfillEventReceiptsMutation();
  const { data: bankAccounts = [] } = useGetLedgerAccountsQuery();

  const [bankAccountId, setBankAccountId] = useState("");
  const [result, setResult] = useState<{ ok: boolean; text: string; skipped?: any[] } | null>(null);

  const count: number = missing?.count ?? 0;
  // Rows carrying no bank account of their own. The ledger would otherwise file
  // them against the gateway settlement account, so one has to be chosen.
  const needsAccount: number = missing?.needs_account ?? 0;

  if (count === 0) return null;

  const run = async () => {
    setResult(null);
    try {
      const res: any = await backfill({
        eventId,
        ...(bankAccountId ? { bank_account_id: bankAccountId } : {}),
      }).unwrap();
      setResult({
        ok: (res?.data?.created ?? 0) > 0,
        text: res?.message ?? "Done.",
        skipped: res?.data?.skipped ?? [],
      });
    } catch (e) {
      setResult({ ok: false, text: extractError(e) });
    }
  };

  return (
    <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:p-5 space-y-3">
      <div className="flex items-start gap-3">
        <Receipt className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <div className="text-sm font-black text-amber-900">
            {count} settled registration{count === 1 ? "" : "s"} {count === 1 ? "has" : "have"} no receipt
          </div>
          <p className="text-xs font-medium text-amber-800 mt-0.5">
            {naira(missing?.total)} was taken but never invoiced, so it does not appear in
            finance analysis and the attendees have no receipt. Raising them now creates the
            invoice, the receipt and the ledger entry from what each registration already records.
          </p>
        </div>
      </div>

      {/* Only asked for when some row cannot answer it itself. */}
      {needsAccount > 0 && (
        <div className="space-y-1">
          <label className="text-[10px] font-black text-amber-700 uppercase tracking-widest">
            Bank account for the {needsAccount} registration{needsAccount === 1 ? "" : "s"} that
            {needsAccount === 1 ? " does" : " do"} not record one
          </label>
          <select
            value={bankAccountId}
            onChange={(e) => setBankAccountId(e.target.value)}
            className="w-full border border-amber-200 rounded-lg px-3 py-2.5 text-sm bg-white"
          >
            <option value="">— Select account —</option>
            {(bankAccounts as any[]).map((acc: any) => (
              <option key={acc.id} value={acc.id}>
                {[acc.bank_name, acc.account_name ?? acc.name, acc.account_number].filter(Boolean).join(" — ")}
              </option>
            ))}
          </select>
          <p className="text-[10px] font-medium text-amber-700">
            Rows that already name the account they were paid into keep it — this is only
            used for the ones that do not. Left blank, those are skipped.
          </p>
        </div>
      )}

      {result && (
        <div className={`text-xs font-bold px-3 py-2 rounded-xl border ${
          result.ok
            ? "bg-emerald-50 text-emerald-700 border-emerald-100"
            : "bg-rose-50 text-rose-700 border-rose-100"
        }`}>
          {result.text}
          {/* Named, not just counted — an unexplained "3 could not be done" is
              an instruction to go looking. */}
          {(result.skipped?.length ?? 0) > 0 && (
            <ul className="mt-1.5 space-y-0.5 font-medium">
              {result.skipped!.map((s: any) => (
                <li key={s.registration_id}>· {s.attendee_name}: {s.reason}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <button
        onClick={run}
        disabled={running}
        className="w-full sm:w-auto flex items-center justify-center gap-2 bg-amber-600 text-white font-black text-xs uppercase tracking-widest px-5 py-3 rounded-xl hover:bg-amber-700 disabled:opacity-50 transition-colors"
      >
        {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Receipt className="w-3.5 h-3.5" />}
        Generate {count} receipt{count === 1 ? "" : "s"}
      </button>
    </div>
  );
}

// ── Registrations Panel ───────────────────────────────────────────────────────
function RegistrationsPanel({ eventId, days }: { eventId: string; days: any[] }) {
  const { data: regs = [], isLoading, refetch } = useAdminGetEventRegistrationsQuery(eventId);
  const { data: eventMaterials = [] }           = useAdminGetMaterialsQuery(eventId);
  const { data: eventRefreshments = [] }        = useAdminGetRefreshmentsQuery(eventId);

  // Guests are not registrations — see the GuestsTable comment below — so they
  // come from their own endpoint rather than out of the list above. It answers
  // with the desk's view of a card (who issued it, whether it was paid for,
  // its receipt), which the report's read-only slice does not carry.
  const { data: guests = [] } = useAdminGetEventGuestsQuery(eventId);

  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const filtered = (regs as any[]).filter((r) => registrationMatches(r, search));

  // Split by what the desk actually needs to act on. One flat list made a
  // delegate who still owes money indistinguishable from one who has paid,
  // which is the difference that decides whether they are let through the door.
  //
  // Paid splits again by SOURCE, because the two settle differently: money the
  // secretariat took at the desk has to be reconciled against a bank account,
  // while gateway money is already settled. `source` is derived from
  // recorded_by server-side — an offline payment writes a Payment row exactly
  // like the gateway does, so nothing else here can tell them apart.
  const paidOnline = filtered.filter((r: any) => r.payment_status === "paid" && r.source !== "admin");
  const paidAdmin  = filtered.filter((r: any) => r.payment_status === "paid" && r.source === "admin");
  const pending    = filtered.filter((r: any) => r.payment_status === "pending");
  const free       = filtered.filter((r: any) => r.payment_status === "free");

  if (isLoading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-indigo-400 animate-spin" /></div>;

  return (
    <div>
      {/* Header + Search */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
        <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
          Registrations ({(regs as any[]).length})
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <input
            className="border-b-2 border-gray-100 focus:border-indigo-500 outline-none text-sm font-bold text-gray-700 py-2 sm:py-1 bg-transparent placeholder-gray-300 w-full sm:w-56"
            placeholder="Name, member no, email, reg no…"
            title="Search by name, membership number, email address, phone or registration number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {/* Bulk upload sits behind the same gate as the single-registration
              form: importing a settled sheet takes money exactly as recording
              one payment at the desk does. */}
          <AccessGate perm={PERMS.event.registrationManage} roles={["admin", "super_admin"]}>
            <button
              onClick={() => setShowImport(true)}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 sm:py-2 rounded-lg border border-gray-200 text-gray-600 text-[10px] font-black uppercase tracking-widest hover:border-indigo-300 hover:text-indigo-600 transition-colors whitespace-nowrap"
            >
              <Upload className="w-3.5 h-3.5" /> Upload Excel
            </button>
          </AccessGate>
          <button
            onClick={() => setShowAdd(true)}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 sm:py-2 rounded-lg bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest hover:bg-indigo-600 transition-colors whitespace-nowrap"
          >
            <Plus className="w-3.5 h-3.5" /> Register / Record Payment
          </button>
        </div>
      </div>

      {showAdd && <AddRegistrationModal eventId={eventId} onClose={() => setShowAdd(false)} />}
      {showAddGuest && <AddGuestModal eventId={eventId} onClose={() => setShowAddGuest(false)} />}
      {showImport && (
        <ImportRegistrationsModal
          eventId={eventId}
          onClose={() => setShowImport(false)}
          onImported={refetch}
        />
      )}

      {/* Backfilling raises invoices, payments and receipts for money already
          taken, so the banner follows the same rule as Record payment. */}
      <AccessGate perm={PERMS.event.registrationManage} roles={["admin", "super_admin"]}>
        <MissingReceiptsBanner eventId={eventId} />
      </AccessGate>

      {filtered.length === 0 && (
        <p className="text-sm text-gray-400 font-medium text-center py-6">
          {search ? "No matching registrations." : "No registrations yet."}
        </p>
      )}

      {/* Paid first — it is the door list. Awaiting payment is the one the desk
          has to chase, so it sits directly under it rather than being buried. */}
      <div className="space-y-6">
        <RegistrantGroup
          title="Paid online"
          tone="emerald"
          rows={paidOnline}
          eventId={eventId}
          days={days}
          eventMaterials={eventMaterials as any[]}
          eventRefreshments={eventRefreshments as any[]}
          onChanged={refetch}
        />
        <RegistrantGroup
          title="Paid — recorded by admin"
          tone="sky"
          rows={paidAdmin}
          eventId={eventId}
          days={days}
          eventMaterials={eventMaterials as any[]}
          eventRefreshments={eventRefreshments as any[]}
          onChanged={refetch}
        />
        <RegistrantGroup
          title="Awaiting payment"
          tone="amber"
          rows={pending}
          eventId={eventId}
          days={days}
          eventMaterials={eventMaterials as any[]}
          eventRefreshments={eventRefreshments as any[]}
          onChanged={refetch}
        />
        <RegistrantGroup
          title="Free / complimentary"
          tone="gray"
          rows={free}
          eventId={eventId}
          days={days}
          eventMaterials={eventMaterials as any[]}
          eventRefreshments={eventRefreshments as any[]}
          onChanged={refetch}
        />

        {/* The desk's own view of the guest list: it can add to it, and remove a
            card it has not taken money for. */}
        <GuestsTable
          guests={guests as any[]}
          search={search}
          eventId={eventId}
          onAdd={() => setShowAddGuest(true)}
        />
      </div>
    </div>
  );
}

/**
 * The post-event record: what the event took, who turned up, what was handed
 * out. Built to leave the screen — it exports to CSV and prints — because this
 * is what the secretariat files and hands to leadership.
 */
function ReportPanel({ eventId }: { eventId: string }) {
  // The filter set the whole panel reads. Held here rather than inside the
  // filter bar because the figures, the rows and the CSV all have to answer the
  // same question — a report whose totals and rows disagree is worse than one
  // that cannot be filtered at all.
  const [filters, setFilters] = useState<Omit<EventReportArgs, "eventId">>({
    q: "", status: "", source: "", recorded_by: "", diet: "active", in_diet: false,
  });

  // Typing is debounced into the request: a keystroke per fetch would re-run the
  // aggregates over the whole delegate list on every letter.
  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => ({ ...f, q: searchInput })), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const args: EventReportArgs = { eventId, ...filters };
  const { data: report, isLoading, isFetching } = useAdminGetEventReportQuery(args);

  const [exporting, setExporting] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const set = (patch: Partial<EventReportArgs>) => setFilters((f) => ({ ...f, ...patch }));

  const reset = () => {
    setSearchInput("");
    setFilters({ q: "", status: "", source: "", recorded_by: "", diet: "active", in_diet: false });
  };

  const isFiltered =
    !!filters.q || !!filters.status || !!filters.source || !!filters.recorded_by || !!filters.in_diet;

  // RTK Query returns parsed JSON, so the CSV is fetched directly — the token
  // still has to be attached by hand, exactly as the finance export does. The
  // filters ride along so the sheet is exactly what is on screen.
  const download = async (scope: "attendees" | "guests" | "categories") => {
    setExporting(scope);
    setExportError(null);
    try {
      const token = localStorage.getItem("token");
      const qs = eventReportQueryString(args, { scope });
      const res = await fetch(`${BASE_URL}/admin/events/${eventId}/report/export?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Mirrors the server's filename: a filtered sheet that is named like the
      // full one gets filed as the full one.
      const tag = isFiltered ? "-filtered" : "";
      a.download = `${(report?.event?.title ?? "event").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${scope}${tag}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setExportError(e?.message ?? "Could not download the export.");
    } finally {
      setExporting(null);
    }
  };

  if (isLoading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-indigo-400 animate-spin" /></div>;
  if (!report)   return <p className="text-sm text-gray-400 text-center py-6">Report unavailable.</p>;

  const ev   = report.event ?? {};
  const regs = report.registrations ?? {};
  const rev  = report.revenue ?? {};
  const att  = report.attendance ?? {};
  const rows: any[] = report.rows ?? [];
  const cats = report.categories ?? {};
  const opts = report.options ?? {};
  const recorders: any[] = opts.recorders ?? [];
  const diets: any[] = opts.diets ?? [];
  const diet = report.upgrade_diet ?? {};

  // Guest cards are not registrations, so a delegate-source filter hides them
  // and the delegate table has nothing to show for 'guest'.
  const guestsOnly = filters.source === "guest";

  return (
    <div className="space-y-6 print:space-y-4" id="event-report">
      {/* Actions — hidden on paper, where they are just noise. */}
      <div className="flex items-center justify-between gap-3 flex-wrap print:hidden">
        <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
          Generated {report.generated_at}
          {isFetching && <Loader2 className="w-3 h-3 text-indigo-400 animate-spin" />}
        </div>
        <div className="w-full sm:w-auto flex items-center gap-2 flex-wrap">
          <button
            onClick={() => download("attendees")}
            disabled={exporting !== null || guestsOnly}
            title={guestsOnly ? "Showing guest cards only — export the guest list instead" : undefined}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 py-2.5 sm:py-2 rounded-lg bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest hover:bg-indigo-600 transition-colors disabled:opacity-50"
          >
            {exporting === "attendees" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Delegates CSV
          </button>
          {/* Offered whenever guests are in scope, not only when the current
              filter left some — an empty guest sheet is a meaningful answer,
              and hiding the button mid-filter looks like a broken export. */}
          {!["admin", "online", "self"].includes(String(filters.source)) && (
            <button
              onClick={() => download("guests")}
              disabled={exporting !== null}
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 py-2.5 sm:py-2 rounded-lg bg-violet-600 text-white text-[10px] font-black uppercase tracking-widest hover:bg-violet-700 transition-colors disabled:opacity-50"
            >
              {exporting === "guests" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              Guests CSV
            </button>
          )}
          {/* The takings sheet: the category summary, the payments behind it,
              and the guest cards — the one export that answers "what did this
              event make and who paid it" without being read alongside another. */}
          <button
            onClick={() => download("categories")}
            disabled={exporting !== null}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 py-2.5 sm:py-2 rounded-lg bg-emerald-600 text-white text-[10px] font-black uppercase tracking-widest hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            {exporting === "categories" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Payments CSV
          </button>
          <button
            onClick={() => window.print()}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 py-2.5 sm:py-2 rounded-lg border border-gray-200 text-gray-700 text-[10px] font-black uppercase tracking-widest hover:bg-gray-50 transition-colors"
          >
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
        </div>
      </div>

      {/* Filters. Every figure below is computed over what these leave, and the
          CSV takes the identical filters — the sheet is what is on screen. */}
      <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-3 print:hidden">
        <div className="flex items-stretch sm:items-center gap-2 flex-col sm:flex-row sm:flex-wrap">
          <div className="relative w-full sm:flex-1 sm:min-w-[200px]">
            <Search className="w-3.5 h-3.5 text-gray-300 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              className="w-full pl-8 pr-3 py-2.5 sm:py-2 rounded-lg border border-gray-200 bg-white text-sm font-bold text-gray-700 placeholder-gray-300 outline-none focus:border-indigo-400"
              placeholder="Search member, membership no, email, reg no, reference, guest…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          <FilterSelect
            value={String(filters.source ?? "")}
            onChange={(v) => set({ source: v as any, recorded_by: v === "admin" ? filters.recorded_by : "" })}
            options={[
              { value: "",       label: "Everyone" },
              { value: "online", label: "Paid online by members" },
              { value: "admin",  label: "Recorded/uploaded by admin" },
              { value: "self",   label: "Self-registered, unpaid" },
              { value: "guest",  label: "Guest cards only" },
            ]}
          />

          {/* Which admin took it. Only meaningful once the desk has entered
              something, so the selector is not shown on an event where it
              would have one option. */}
          {recorders.length > 0 && (
            <FilterSelect
              value={String(filters.recorded_by ?? "")}
              onChange={(v) => set({ recorded_by: v, source: v ? "admin" : filters.source })}
              options={[
                { value: "", label: "Any admin" },
                ...recorders.map((r: any) => ({ value: String(r.id), label: `${r.name} (${r.count})` })),
              ]}
            />
          )}

          <FilterSelect
            value={String(filters.status ?? "")}
            onChange={(v) => set({ status: v as any })}
            options={[
              { value: "",        label: "Any payment status" },
              { value: "paid",    label: "Paid" },
              { value: "pending", label: "Awaiting payment" },
              { value: "free",    label: "Free / complimentary" },
            ]}
          />

          {/* Picking a cycle NARROWS the report to the applicants sitting in
              it — choosing one and still being shown the whole event is not
              what the choice reads as. The opening state ('active', untouched)
              only tags them, because that is the default view of the event
              rather than a choice anyone made. */}
          {diets.length > 0 && (
            <FilterSelect
              value={String(filters.diet ?? "active")}
              onChange={(v) => set({ diet: v, in_diet: v !== "none" })}
              options={[
                { value: "active", label: "Current review cycle" },
                ...diets.map((d: any) => ({
                  value: d.id,
                  label: `${d.title}${d.ongoing ? " (ongoing)" : ""}`,
                })),
                { value: "none", label: "No review cycle" },
              ]}
            />
          )}

          {/* Untick to keep the cycle's column while widening back to the whole
              event — the one thing selecting a cycle no longer leaves reachable. */}
          {diet.id && (
            <label className="inline-flex items-center gap-2 px-3 py-2.5 sm:py-2 rounded-lg border border-gray-200 bg-white text-[11px] font-black uppercase tracking-widest text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                className="accent-indigo-600"
                checked={!!filters.in_diet}
                onChange={(e) => set({ in_diet: e.target.checked })}
              />
              Cycle only
            </label>
          )}

          {isFiltered && (
            <button
              onClick={reset}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 sm:py-2 rounded-lg border border-gray-200 bg-white text-gray-600 text-[10px] font-black uppercase tracking-widest hover:bg-gray-50"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          )}
        </div>

        {isFiltered && (
          <p className="text-[11px] text-gray-500 font-bold mt-2 flex items-center gap-1.5">
            <SlidersHorizontal className="w-3 h-3 text-indigo-400" />
            Every figure and export below covers the filtered set only —
            {" "}{rows.length} {rows.length === 1 ? "delegate" : "delegates"}
            {(report.guests?.length ?? 0) > 0 && ` and ${report.guests.length} guest card${report.guests.length === 1 ? "" : "s"}`}.
          </p>
        )}

        {/* Says why the cycle column is missing. Without this the report just
            silently omits it, and the reader cannot tell whether nobody in the
            cycle came or whether no cycle was being measured. */}
        {!diet.id && diet.reason && (
          <p className="text-[11px] text-gray-400 font-bold mt-2 flex items-center gap-1.5">
            <GraduationCap className="w-3 h-3" />
            {diet.reason}
            {diets.length > 0 && " Pick one above to measure this event against it."}
          </p>
        )}
      </div>

      {exportError && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-rose-50 border border-rose-100 text-rose-700 text-sm font-bold print:hidden">
          <AlertCircle className="w-4 h-4 shrink-0" /> {exportError}
        </div>
      )}

      {/* Printed sheets leave the building without their tab, so the report
          names the event it belongs to. Hidden on screen — the page header
          already says all this. */}
      <div className="hidden print:block border-b border-gray-300 pb-3">
        <h1 className="text-lg font-black text-gray-900">{ev.title}</h1>
        <p className="text-xs text-gray-500">
          {ev.start_date}{ev.end_date && ev.end_date !== ev.start_date ? ` — ${ev.end_date}` : ""}
          {ev.location ? ` · ${ev.location}` : ""}
        </p>
      </div>

      {/* Registrations */}
      <ReportSection title="Registrations">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Total" value={regs.total ?? 0} />
          <Stat label="Paid" value={regs.paid ?? 0} tone="emerald" />
          <Stat label="Awaiting payment" value={regs.pending ?? 0} tone="amber" />
          <Stat label="Free" value={regs.free ?? 0} />
        </div>
      </ReportSection>

      {/* Where the registrations came from. Kept apart from the payment-status
          block above: they answer different questions — one is whether the
          money arrived, the other is who put it in the system. */}
      <ReportSection title="How they registered">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Stat label="Entered by admin" value={regs.by_source?.admin ?? 0} tone="sky" />
          <Stat label="Paid online" value={regs.by_source?.online ?? 0} tone="emerald" />
          <Stat label="Self-registered, unpaid" value={regs.by_source?.self ?? 0} tone="amber" />
        </div>
        <p className="text-[11px] text-gray-400 mt-2">
          Of the {regs.paid ?? 0} paid, {regs.paid_by_source?.admin ?? 0} were taken at the desk
          ({naira(rev.collected_by_source?.admin)}) and {regs.paid_by_source?.online ?? 0} through
          the gateway ({naira(rev.collected_by_source?.online)}). Desk takings still have to be
          reconciled against a bank account.
        </p>

        {/* Per-admin, because "how much did the desk take" is not the figure
            that reconciles — each person on the desk answers for their own. */}
        {(regs.by_recorder?.length ?? 0) > 0 && (
          <div className="overflow-x-auto rounded-xl border border-gray-100 mt-4">
            <table className="w-full min-w-[30rem] print:min-w-0 text-sm">
              <thead>
                <tr className="text-left text-[10px] font-black text-gray-400 uppercase tracking-widest bg-gray-50 border-b border-gray-100">
                  <th className="px-3 py-2.5">Recorded / uploaded by</th>
                  <th className="px-3 py-2.5">Paid registrations</th>
                  <th className="px-3 py-2.5">Amount</th>
                  <th className="px-3 py-2.5 print:hidden"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {regs.by_recorder.map((r: any) => (
                  <tr key={r.id}>
                    <td className="px-3 py-2.5 font-bold text-gray-700">{r.name}</td>
                    <td className="px-3 py-2.5 text-gray-600">{r.count}</td>
                    <td className="px-3 py-2.5 text-gray-800 font-bold">{naira(r.amount)}</td>
                    <td className="px-3 py-2.5 text-right print:hidden">
                      <button
                        onClick={() => set({ source: "admin", recorded_by: r.id, status: "paid" })}
                        className="text-[10px] font-black uppercase tracking-widest text-indigo-600 hover:underline"
                      >
                        Filter
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ReportSection>

      {/* Money */}
      <ReportSection title="Revenue">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Collected" value={naira(rev.collected)} tone="emerald" />
          <Stat label="Guest cards" value={naira(rev.guest_cards)} tone="violet" />
          <Stat label="Total taken" value={naira(rev.total)} />
          <Stat label="Outstanding" value={naira(rev.outstanding)} tone="amber" />
        </div>

        {rev.outstanding_is_estimate && Number(rev.outstanding) > 0 && (
          <p className="text-[11px] text-gray-400 mt-2">
            Outstanding is priced off the event fee. Tiered events charge per category, so treat it
            as an indication rather than a ledger figure.
          </p>
        )}

        {(rev.by_method?.length ?? 0) > 0 && (
          <div className="overflow-x-auto rounded-xl border border-gray-100 mt-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-black text-gray-400 uppercase tracking-widest bg-gray-50 border-b border-gray-100">
                  <th className="px-3 py-2.5">Payment method</th>
                  <th className="px-3 py-2.5">Count</th>
                  <th className="px-3 py-2.5">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {report.revenue.by_method.map((m: any) => (
                  <tr key={m.method}>
                    <td className="px-3 py-2.5 font-bold text-gray-700 capitalize">{String(m.method).replace(/_/g, " ")}</td>
                    <td className="px-3 py-2.5 text-gray-600">{m.count}</td>
                    <td className="px-3 py-2.5 text-gray-800 font-bold">{naira(m.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ReportSection>

      {/* Who paid, grouped by the category they were charged as.
          Leadership does not read an event's takings as one number — what the
          fellows brought against what the graduates brought is the figure that
          sets next year's fees — and the same question is always asked twice:
          the total, then the names behind it. Both live in this one section. */}
      {(cats.rows?.length ?? 0) > 0 && (
        <ReportSection title="Payments by category">
          <CategoryPaymentsTable categories={cats} guests={report.guests ?? []} />
        </ReportSection>
      )}

      {/* What the upgrade cohort brought in.
          An induction event is largely paid for by the applicants sitting in
          the current review cycle — settling their post-recommendation bundle
          registers them and marks the fee paid — so leadership asks how much of
          the gate came from that cohort. It is not derivable from the event
          alone; it needs the applications' cycle. */}
      {diet.id && (
        <ReportSection title="Upgrade review cycle">
          <div className="rounded-2xl border border-indigo-100 bg-indigo-50/50 p-3">
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <GraduationCap className="w-4 h-4 text-indigo-500" />
              <span className="text-sm font-black text-gray-800">{diet.title}</span>
              {(diet.is_active || diet.status === "ongoing") && (
                <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700">
                  Ongoing
                </span>
              )}
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                {diet.applicants} applicant{diet.applicants === 1 ? "" : "s"} in the cycle
              </span>
              {!filters.in_diet && (
                <button
                  onClick={() => set({ in_diet: true })}
                  className="ml-auto text-[10px] font-black uppercase tracking-widest text-indigo-600 hover:underline print:hidden"
                >
                  Show only this cycle
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Registered from cycle" value={diet.registrations?.total ?? 0} tone="violet" />
              <Stat label="Paid from cycle" value={diet.registrations?.paid ?? 0} tone="emerald" />
              <Stat label="Payments from cycle" value={naira(diet.registrations?.amount)} tone="violet" />
              <Stat
                label="Guest cards from cycle"
                value={`${diet.guests?.total ?? 0} · ${naira(diet.guests?.amount)}`}
                tone="violet"
              />
            </div>

            <p className="text-[11px] text-gray-500 mt-2">
              Counted over the rows shown above. {Math.max(0, (diet.applicants ?? 0) - (diet.registrations?.total ?? 0))} of
              the cycle's applicants have no registration on this event.
            </p>
          </div>
        </ReportSection>
      )}

      {/* Attendance */}
      <ReportSection title="Attendance">
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Present" value={att.overall?.present ?? 0} tone="emerald" />
          <Stat label="Partial" value={att.overall?.partial ?? 0} tone="amber" />
          <Stat label="Absent" value={att.overall?.absent ?? 0} />
        </div>

        {(att.by_day?.length ?? 0) > 0 && (
          <div className="overflow-x-auto rounded-xl border border-gray-100 mt-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-black text-gray-400 uppercase tracking-widest bg-gray-50 border-b border-gray-100">
                  <th className="px-3 py-2.5">Day</th>
                  <th className="px-3 py-2.5">Present</th>
                  <th className="px-3 py-2.5">Absent</th>
                  <th className="px-3 py-2.5">Turnout</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {att.by_day.map((d: any) => {
                  const pct = att.expected > 0 ? Math.round((d.present / att.expected) * 100) : 0;
                  return (
                    <tr key={d.event_day_id}>
                      <td className="px-3 py-2.5 font-bold text-gray-700">
                        {d.day_label}
                        <span className="block text-[11px] text-gray-400 font-medium">{d.day_date}</span>
                      </td>
                      <td className="px-3 py-2.5 text-emerald-700 font-bold">{d.present}</td>
                      <td className="px-3 py-2.5 text-gray-500">{d.absent}</td>
                      <td className="px-3 py-2.5 text-gray-600">{pct}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </ReportSection>

      {/* CPD */}
      {report.cpd?.is_cpd && (
        <ReportSection title="CPD">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Points per attendee" value={report.cpd.points} tone="violet" />
            <Stat label="Awarded to" value={`${report.cpd.awarded_count} attendees`} tone="violet" />
          </div>
        </ReportSection>
      )}

      {/* Materials */}
      {(report.materials?.length ?? 0) > 0 && (
        <ReportSection title="Materials issued">
          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-black text-gray-400 uppercase tracking-widest bg-gray-50 border-b border-gray-100">
                  <th className="px-3 py-2.5">Material</th>
                  <th className="px-3 py-2.5">Issued</th>
                  <th className="px-3 py-2.5">Outstanding</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {report.materials.map((m: any) => (
                  <tr key={m.id}>
                    <td className="px-3 py-2.5 font-bold text-gray-700">{m.name}</td>
                    <td className="px-3 py-2.5 text-gray-600">{m.issued} / {m.expected}</td>
                    <td className="px-3 py-2.5 text-gray-500">{Math.max(0, m.expected - m.issued)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ReportSection>
      )}

      {/* Refreshments */}
      {(report.refreshments?.length ?? 0) > 0 && (
        <ReportSection title="Refreshments">
          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-black text-gray-400 uppercase tracking-widest bg-gray-50 border-b border-gray-100">
                  <th className="px-3 py-2.5">Refreshment</th>
                  <th className="px-3 py-2.5">Chosen</th>
                  <th className="px-3 py-2.5">Collected</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {report.refreshments.map((r: any) => (
                  <tr key={r.id}>
                    <td className="px-3 py-2.5 font-bold text-gray-700">{r.name}</td>
                    <td className="px-3 py-2.5 text-gray-600">{r.chosen}</td>
                    <td className="px-3 py-2.5 text-gray-600">{r.collected}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ReportSection>
      )}

      {/* The rows the figures above were computed from, and the rows the CSV
          will contain. Shown after the aggregates because the report is read
          top-down, but it is the part the filters are actually aimed at. */}
      {!guestsOnly && (
        <ReportSection title={`Delegates (${rows.length})`}>
          <ReportRowsTable rows={rows} hasDiet={!!diet.id} />
        </ReportSection>
      )}

      {/* Guests. Rendered even when empty under a guest filter — "no guest
          cards match" is an answer, while a vanished section reads as a bug. */}
      {((report.guests?.length ?? 0) > 0 || guestsOnly) && (
        <ReportSection title={`Guest access cards (${report.guests?.length ?? 0})`}>
          {(report.guests?.length ?? 0) > 0
            ? <GuestsTable guests={report.guests} search="" showDiet={!!diet.id} />
            : <p className="text-sm text-gray-400 font-medium py-4">No guest cards match this filter.</p>}
        </ReportSection>
      )}
    </div>
  );
}

/** A compact labelled `<select>` — the report's filter bar uses several. */
function FilterSelect({
  value, onChange, options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full sm:w-auto sm:max-w-[16rem] px-3 py-2.5 sm:py-2 rounded-lg border border-gray-200 bg-white text-[11px] font-black uppercase tracking-widest text-gray-600 outline-none focus:border-indigo-400 cursor-pointer"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

/**
 * Takings per price category, each row opening onto the payments behind it.
 *
 * The names are collapsed on screen and printed in full: on a monitor this is
 * a summary table someone drills into, on paper it is the schedule that backs
 * the total — a printed figure nobody can trace back to a list of payers is
 * exactly what the secretariat gets asked to re-derive by hand.
 *
 * Categories with no payments are still listed. The event's fee list is part of
 * the answer, and "no fellow paid" reads very differently from a missing row.
 */
function CategoryPaymentsTable({ categories, guests }: { categories: any; guests: any[] }) {
  const rows: any[] = categories.rows ?? [];
  const [open, setOpen] = useState<string[]>([]);

  const paidCount   = categories.totals?.paid ?? 0;
  const paidAmount  = Number(categories.totals?.amount ?? 0);
  const guestCount  = guests.length;
  const guestAmount = guests.reduce((sum, g) => sum + Number(g.amount || 0), 0);

  const toggle = (key: string) =>
    setOpen((o) => (o.includes(key) ? o.filter((k) => k !== key) : [...o, key]));

  return (
    <div>
      <div className="overflow-x-auto rounded-2xl border border-gray-100">
        <table className="w-full min-w-[38rem] print:min-w-0 text-sm">
          <thead>
            <tr className="text-left text-[10px] font-black text-gray-400 uppercase tracking-widest bg-gray-50 border-b border-gray-100">
              <th className="px-3 py-2.5">Category</th>
              <th className="px-3 py-2.5">Fee</th>
              <th className="px-3 py-2.5">Payments</th>
              <th className="px-3 py-2.5">Amount</th>
              <th className="px-3 py-2.5 print:hidden"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {rows.map((c) => {
              const isOpen = open.includes(c.key);
              const payers: any[] = c.payers ?? [];

              return (
                <Fragment key={c.key}>
                  <tr className={c.paid === 0 ? "text-gray-400" : ""}>
                    <td className="px-3 py-2.5 font-bold text-gray-700">{c.label}</td>
                    {/* A category the event never priced — an induction seat is
                        priced on the applicant's upgrade bundle, not the fee
                        list — still has a fee: what was collected, per seat.
                        Marked as an average so it is never read as a published
                        price the desk can quote. */}
                    <td className="px-3 py-2.5 text-gray-600">
                      {c.price === null || c.price === undefined ? "—" : naira(c.price)}
                      {c.price_is_derived && (
                        <span className="block text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                          Avg per seat
                        </span>
                      )}
                      {/* A category priced across the fee timeline has three
                          published fees; this names the one being shown so it
                          is never quoted as the whole schedule. */}
                      {!c.price_is_derived && c.price_window && (
                        <span className="block text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                          {WINDOW_LABELS[c.price_window] ?? c.price_window} rate
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-gray-600">{c.paid}</td>
                    <td className="px-3 py-2.5 font-black text-gray-800">{naira(c.amount)}</td>
                    <td className="px-3 py-2.5 text-right print:hidden">
                      {payers.length > 0 && (
                        <button
                          onClick={() => toggle(c.key)}
                          className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-indigo-600 hover:underline"
                        >
                          {isOpen ? "Hide" : "Who paid"}
                          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        </button>
                      )}
                    </td>
                  </tr>

                  {payers.length > 0 && (
                    <tr className={isOpen ? "" : "hidden print:table-row"}>
                      <td colSpan={5} className="px-3 pb-3 bg-gray-50/60">
                        <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest py-2">
                          {c.label} — {payers.length} payment{payers.length === 1 ? "" : "s"}
                        </div>
                        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white">
                          <table className="w-full min-w-[34rem] print:min-w-0 text-sm">
                            <thead>
                              <tr className="text-left text-[9px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                                <th className="px-3 py-2">SN</th>
                                <th className="px-3 py-2">Member</th>
                                <th className="px-3 py-2">Reg no</th>
                                <th className="px-3 py-2">Reference</th>
                                <th className="px-3 py-2">Paid</th>
                                <th className="px-3 py-2">Amount</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {payers.map((p, i) => (
                                <tr key={p.id}>
                                  <td className="px-3 py-2 text-[11px] text-gray-400">{i + 1}</td>
                                  <td className="px-3 py-2">
                                    <span className="font-bold text-gray-800">{p.name || "—"}</span>
                                    <span className="block text-[11px] text-gray-400">
                                      {p.membership_no || "—"}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 font-mono text-[11px] text-gray-500">
                                    {p.registration_no || "—"}
                                  </td>
                                  <td className="px-3 py-2 font-mono text-[11px] text-gray-500">
                                    {p.payment_reference || "—"}
                                    {p.payment_method && (
                                      <span className="block font-sans capitalize text-gray-400">
                                        {String(p.payment_method).replace(/_/g, " ")}
                                        {p.recorded_by_name ? ` · ${p.recorded_by_name}` : ""}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-[11px] text-gray-500">{p.paid_at || "—"}</td>
                                  <td className="px-3 py-2 font-bold text-gray-800">{naira(p.amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 border-t border-gray-100">
              <td className="px-3 py-2.5 text-[10px] font-black text-gray-400 uppercase tracking-widest" colSpan={2}>
                {rows.length} {rows.length === 1 ? "category" : "categories"}
              </td>
              <td className="px-3 py-2.5 font-black text-gray-800">{paidCount}</td>
              <td className="px-3 py-2.5 font-black text-gray-800" colSpan={2}>{naira(paidAmount)}</td>
            </tr>

            {/* Guest cards are money the event took but not a registration
                anyone can be charged a category for, so they close the table on
                their own line rather than being folded into a category. Hidden
                when nobody bought one — a zero line invites the question of
                which category it belongs to. */}
            {guestCount > 0 && (
              <>
                <tr className="bg-gray-50">
                  <td className="px-3 py-2.5 text-[10px] font-black text-gray-400 uppercase tracking-widest" colSpan={2}>
                    Guest access cards
                  </td>
                  <td className="px-3 py-2.5 font-bold text-gray-600">{guestCount}</td>
                  <td className="px-3 py-2.5 font-bold text-gray-600" colSpan={2}>{naira(guestAmount)}</td>
                </tr>
                <tr className="bg-gray-100 border-t border-gray-200">
                  <td className="px-3 py-3 text-[10px] font-black text-gray-500 uppercase tracking-widest" colSpan={2}>
                    Total taken
                  </td>
                  <td className="px-3 py-3 font-black text-gray-900">{paidCount + guestCount}</td>
                  <td className="px-3 py-3 font-black text-gray-900" colSpan={2}>
                    {naira(paidAmount + guestAmount)}
                  </td>
                </tr>
              </>
            )}
          </tfoot>
        </table>
      </div>

      <p className="text-[11px] text-gray-400 mt-2">
        {categories.is_tiered
          ? "Categories come from the event's fee list; a delegate the desk entered without picking a tier is counted under their membership grade."
          : "This event has no per-category fee list, so payments are grouped by the payer's membership grade."}
        {rows.some((c: any) => c.price_is_derived) &&
          " A fee marked as an average was not published on this event — inductees are priced on their upgrade bundle — so it is the takings divided by the seats paid for."}
        {rows.some((c: any) => c.price_window) &&
          " A category on the fee timeline shows the fee in force today; delegates who paid in an earlier window paid that window's fee."}
        {" "}Guest access cards are not registrations and are listed separately.
      </p>
    </div>
  );
}

const ROW_STATUS_TONES: Record<string, string> = {
  paid:    "bg-emerald-50 text-emerald-700",
  pending: "bg-amber-50 text-amber-700",
  free:    "bg-gray-100 text-gray-500",
};

const ROW_SOURCE_LABELS: Record<string, string> = {
  admin:  "Admin",
  online: "Online",
  self:   "Self",
};

/**
 * The filtered delegate list, paginated.
 *
 * Paginated in the browser rather than on the server: the rows arrive already
 * narrowed, and printing has to put the whole list on paper — a server page
 * would print one screenful and call it the register.
 */
function ReportRowsTable({ rows, hasDiet }: { rows: any[]; hasDiet: boolean }) {
  const [page, setPage] = useState(1);
  const PER_PAGE = 25;

  // A filter that shrinks the list can strand the view on a page past the end.
  useEffect(() => { setPage(1); }, [rows.length]);

  if (rows.length === 0) {
    return <p className="text-sm text-gray-400 font-medium py-4">No delegates match this filter.</p>;
  }

  const totalPages = Math.max(1, Math.ceil(rows.length / PER_PAGE));
  const total      = rows.reduce((sum, r) => sum + Number(r.amount_paid || 0), 0);

  return (
    <div>
      {/* Seven columns need more room than a phone has. `min-w` makes the
          table scroll inside its own box instead of crushing every column;
          dropped for print, where the page is wider than the constraint. */}
      <div className="overflow-x-auto rounded-2xl border border-gray-100">
        <table className="w-full min-w-[44rem] print:min-w-0 text-sm">
          <thead>
            <tr className="text-left text-[10px] font-black text-gray-400 uppercase tracking-widest bg-gray-50 border-b border-gray-100">
              <th className="px-3 py-2.5">Member</th>
              <th className="px-3 py-2.5">Reg no</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Source</th>
              <th className="px-3 py-2.5">Recorded by</th>
              <th className="px-3 py-2.5">Amount</th>
              <th className="px-3 py-2.5">Attendance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {/* Printing takes the whole register, not the page being looked at.
                The rest of the rows are hidden on screen and revealed on paper. */}
            {rows.map((r, i) => {
              const onPage = i >= (page - 1) * PER_PAGE && i < page * PER_PAGE;
              return (
                <tr key={r.id} className={onPage ? "" : "hidden print:table-row"}>
                  <td className="px-3 py-2.5">
                    <span className="font-bold text-gray-800">{r.name || "—"}</span>
                    <span className="block text-[11px] text-gray-400">
                      {r.membership_no || r.email || "—"}
                    </span>
                    {hasDiet && r.in_upgrade_diet && (
                      <span className="inline-flex items-center gap-1 mt-1 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600">
                        <GraduationCap className="w-2.5 h-2.5" /> Upgrade cycle
                      </span>
                    )}
                    {/* The card the delegate uploaded to claim a restricted
                        rate. A link, not a tick: the point is to open it. */}
                    {r.id_card_url && (
                      <a
                        href={r.id_card_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 mt-1 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 hover:bg-sky-100 print:hidden"
                      >
                        ID card
                      </a>
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-[11px] text-gray-500">{r.registration_no || "—"}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md ${ROW_STATUS_TONES[r.payment_status] ?? "bg-gray-100 text-gray-500"}`}>
                      {r.payment_status}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-xs font-bold text-gray-600">
                    {ROW_SOURCE_LABELS[r.source] ?? r.source}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-600">
                    {r.recorded_by_name || "—"}
                    {r.payment_method && (
                      <span className="block text-[11px] text-gray-400 capitalize">
                        {String(r.payment_method).replace(/_/g, " ")}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-bold text-gray-800">{naira(r.amount_paid)}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-600 capitalize">{r.overall_attendance}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 border-t border-gray-100">
              <td className="px-3 py-2.5 text-[10px] font-black text-gray-400 uppercase tracking-widest" colSpan={5}>
                {rows.length} {rows.length === 1 ? "delegate" : "delegates"}
              </td>
              <td className="px-3 py-2.5 font-black text-gray-800" colSpan={2}>{naira(total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 print:hidden">
          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-[10px] font-black uppercase tracking-widest text-gray-600 disabled:opacity-40 hover:bg-gray-50"
            >
              Prev
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-[10px] font-black uppercase tracking-widest text-gray-600 disabled:opacity-40 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReportSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="break-inside-avoid">
      <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2.5">{title}</div>
      {children}
    </div>
  );
}

const STAT_TONES: Record<string, string> = {
  emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
  sky:     "bg-sky-50 border-sky-100 text-sky-700",
  amber:   "bg-amber-50 border-amber-100 text-amber-700",
  violet:  "bg-violet-50 border-violet-100 text-violet-700",
  // Rows an upload cannot take — see ImportRegistrationsModal.
  rose:    "bg-rose-50 border-rose-100 text-rose-700",
  gray:    "bg-gray-50 border-gray-100 text-gray-700",
};

function Stat({ label, value, tone = "gray" }: { label: string; value: React.ReactNode; tone?: string }) {
  return (
    <div className={`rounded-xl border p-3 ${STAT_TONES[tone] ?? STAT_TONES.gray}`}>
      <div className="text-[9px] font-black uppercase tracking-widest opacity-60">{label}</div>
      <div className="text-lg font-black mt-0.5 leading-tight">{value}</div>
    </div>
  );
}

const GROUP_TONES: Record<string, string> = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
  sky:     "bg-sky-50 text-sky-700 border-sky-100",
  amber:   "bg-amber-50 text-amber-700 border-amber-100",
  gray:    "bg-gray-100 text-gray-500 border-gray-100",
};

/**
 * One payment-status section, paginated on its own.
 *
 * Each group keeps its own page rather than sharing one across the whole list:
 * paging the paid table must not move the unpaid one, since the desk works them
 * separately. Renders nothing when the group is empty — an event where everyone
 * has paid should not show an empty "Awaiting payment" heading.
 */
function RegistrantGroup({
  title, tone, rows, eventId, days, eventMaterials, eventRefreshments, onChanged,
}: {
  title: string; tone: string; rows: any[]; eventId: string; days: any[];
  eventMaterials: any[]; eventRefreshments: any[]; onChanged?: () => void;
}) {
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;

  const totalPages = Math.max(1, Math.ceil(rows.length / PER_PAGE));
  const paginated  = rows.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // A search that shrinks the group can strand the view on a page past the end.
  useEffect(() => { setPage(1); }, [rows.length]);

  if (rows.length === 0) return null;

  const total = rows.reduce((sum, r) => sum + Number(r.amount_paid || 0), 0);

  return (
    <div>
      <div className="flex items-center gap-2 mb-2.5 flex-wrap">
        <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg border ${GROUP_TONES[tone] ?? GROUP_TONES.gray}`}>
          {title}
        </span>
        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
          {rows.length} {rows.length === 1 ? "attendee" : "attendees"}
        </span>
        {total > 0 && (
          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
            · {naira(total)}
          </span>
        )}
      </div>

      <div className="space-y-2">
        {paginated.map((r: any) => (
          <RegistrantRow
            key={r.id}
            eventId={eventId}
            reg={r}
            days={days}
            eventMaterials={eventMaterials}
            eventRefreshments={eventRefreshments}
            onChanged={onChanged}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-50">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="text-xs font-black text-gray-400 hover:text-gray-700 disabled:opacity-30 transition-colors px-2 py-1"
          >
            ← Prev
          </button>
          <div className="flex items-center gap-1 flex-wrap justify-center">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                onClick={() => setPage(n)}
                className={`w-7 h-7 rounded-lg text-xs font-black transition-colors ${
                  n === page ? "bg-gray-900 text-white" : "text-gray-400 hover:bg-gray-100"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="text-xs font-black text-gray-400 hover:text-gray-700 disabled:opacity-30 transition-colors px-2 py-1"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Non-member guests admitted on somebody else's access card — bought by an
 * inductee with their induction bundle, or issued at the desk.
 *
 * Deliberately its own table rather than rows in the paid list: a guest is not
 * a delegate. They have no registration number, no attendance to mark, no
 * materials and no CPD — but they did pay, and they will turn up at the gate,
 * so a door list that omits them is wrong.
 *
 * Two callers, and the difference is `onAdd`. The report renders it read-only
 * and shows nothing when nobody bought a card; the registrations panel passes
 * `onAdd` and gets the desk's version — always on screen, because "there are no
 * guests yet" is where issuing the first one starts, with a remove on any card
 * that has not been paid for.
 */
function GuestsTable({
  guests, search, showDiet = false, eventId, onAdd,
}: {
  guests: any[];
  search: string;
  showDiet?: boolean;
  eventId?: string;
  onAdd?: () => void;
}) {
  const [deleteGuest, { isLoading: deleting }] = useAdminDeleteEventGuestMutation();
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [error, setError] = useState("");

  const manage = !!onAdd && !!eventId;

  const q = search.trim().toLowerCase();
  const rows = !q
    ? guests
    : guests.filter((g) =>
        (g.name ?? "").toLowerCase().includes(q) ||
        (g.email ?? "").toLowerCase().includes(q) ||
        (g.host_name ?? "").toLowerCase().includes(q),
      );

  if (rows.length === 0 && !manage) return null;

  const total = rows.reduce((sum, g) => sum + Number(g.amount || 0), 0);

  const remove = async (g: any) => {
    setError("");
    if (!window.confirm(`Remove the guest card issued to ${g.name}?`)) return;
    setRemovingId(g.id);
    try {
      await deleteGuest({ eventId: eventId!, guestId: g.id }).unwrap();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-2.5 flex-wrap">
        <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg border bg-violet-50 text-violet-700 border-violet-100">
          Guest cards
        </span>
        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
          {rows.length} {rows.length === 1 ? "guest" : "guests"} · {naira(total)}
        </span>
        {manage && (
          // Issuing a card takes money and raises a receipt, so it is gated the
          // way recording a payment is rather than on viewing the event.
          <AccessGate perm={PERMS.event.registrationManage} roles={["admin", "super_admin"]}>
            <button
              onClick={onAdd}
              className="ml-auto inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-violet-600 text-white text-[10px] font-black uppercase tracking-widest hover:bg-violet-700 transition-colors whitespace-nowrap"
            >
              <Plus className="w-3.5 h-3.5" /> Add Guest
            </button>
          </AccessGate>
        )}
      </div>

      {error && (
        <div className="mb-2.5 text-xs font-bold text-rose-600 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2">
          {error}
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-gray-400 font-medium text-center py-5 rounded-2xl border border-dashed border-gray-200">
          {search ? "No matching guests." : "No guest cards issued for this event yet."}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-gray-100">
          <table className="w-full min-w-[36rem] print:min-w-0 text-sm">
            <thead>
              <tr className="text-left text-[10px] font-black text-gray-400 uppercase tracking-widest bg-gray-50 border-b border-gray-100">
                <th className="px-3 py-2.5">Guest</th>
                <th className="px-3 py-2.5">Contact</th>
                <th className="px-3 py-2.5">Guest of</th>
                <th className="px-3 py-2.5">Fee</th>
                <th className="px-3 py-2.5">Access code</th>
                {manage && <th className="px-3 py-2.5" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((g) => (
                <tr key={g.id}>
                  <td className="px-3 py-2.5 font-bold text-gray-800">
                    {g.name || "—"}
                    {manage && g.source === "induction" && (
                      <span className="block text-[9px] font-black uppercase tracking-widest text-gray-400 mt-0.5">
                        Induction bundle
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-500">
                    {g.email || "—"}
                    {g.phone && <span className="block">{g.phone}</span>}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-600">
                    {g.host_name || "—"}
                    {g.host_membership_no && (
                      <span className="block text-gray-400">{g.host_membership_no}</span>
                    )}
                    {showDiet && g.in_upgrade_diet && (
                      <span className="inline-flex items-center gap-1 mt-1 text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600">
                        <GraduationCap className="w-2.5 h-2.5" /> Upgrade cycle
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-gray-700">
                    {Number(g.amount || 0) > 0 ? naira(g.amount) : "Complimentary"}
                    {manage && g.receipt_number && (
                      <span className="block text-[10px] font-mono text-gray-400">{g.receipt_number}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 font-mono text-[11px] text-gray-400">{g.access_code}</td>
                  {manage && (
                    <td className="px-3 py-2.5 text-right">
                      {/* Only a card nobody has paid for. Once money is attached
                          removing it would orphan a receipt — that is a refund,
                          which is finance's to do. */}
                      {!g.is_paid && g.source !== "induction" && (
                        <AccessGate perm={PERMS.event.registrationManage}>
                          <button
                            onClick={() => remove(g)}
                            disabled={deleting && removingId === g.id}
                            title="Remove this guest card"
                            className="p-1.5 rounded-lg text-gray-300 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                          >
                            {deleting && removingId === g.id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        </AccessGate>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Issue a guest card at the desk.
 *
 * The money is optional — a complimentary invitation is a card with no fee —
 * but once an amount is being taken it is treated exactly as an offline
 * registration fee: the bank account it landed in is mandatory, because that is
 * what the ledger entry is credited to.
 */
function AddGuestModal({ eventId, onClose }: { eventId: string; onClose: () => void }) {
  const [createGuest, { isLoading: saving }] = useAdminCreateEventGuestMutation();
  const [searchMembers, { data: memberResults = [], isFetching: searching }] = useLazyAdminSearchEventMembersQuery();
  const { data: bankAccounts = [] } = useGetLedgerAccountsQuery();

  // One row per guest. A member who brings two pays for both together, but each
  // of them is a card of their own — its own name and its own access code — so
  // the form collects them individually.
  const [rows, setRows] = useState([{ name: "", email: "", phone: "" }]);
  const [hostQuery, setHostQuery] = useState("");
  const [host, setHost] = useState<any | null>(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [bankAccountId, setBankAccountId] = useState("");
  const [reference, setReference] = useState("");
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [markPaid, setMarkPaid] = useState(true);
  const [proof, setProof] = useState<File | null>(null);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  // What decides whether this call takes money — and so whether the bank
  // account is mandatory. Mirrors the server's own test.
  const settling = Number(amount || 0) > 0 && markPaid;

  // The fee is per guest; the member hands over the total for all of them.
  const named = rows.filter((r) => r.name.trim());
  const total = Number(amount || 0) * Math.max(named.length, 1);

  const setRow = (i: number, patch: Partial<(typeof rows)[number]>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, { name: "", email: "", phone: "" }]);
  const removeRow = (i: number) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const submit = async () => {
    setError(""); setOk("");
    if (!host) { setError("Find the member this guest is attending with by their membership number."); return; }
    if (named.length === 0) { setError("Enter the name of at least one guest."); return; }
    if (settling && !bankAccountId) { setError("Select the bank account this was paid into."); return; }

    const fd = new FormData();
    // Rows left blank are dropped rather than rejected — an empty row is a row
    // the desk added and did not use, not a mistake to be corrected.
    named.forEach((g, i) => {
      fd.append(`guests[${i}][name]`, g.name.trim());
      if (g.email.trim()) fd.append(`guests[${i}][email]`, g.email.trim());
      if (g.phone.trim()) fd.append(`guests[${i}][phone]`, g.phone.trim());
    });
    fd.append("host_user_id", String(host.user_id));
    if (amount !== "") fd.append("amount", amount);
    fd.append("mark_paid", markPaid ? "1" : "0");
    if (settling) {
      fd.append("payment_method", method);
      fd.append("bank_account_id", bankAccountId);
      if (reference.trim()) fd.append("payment_reference", reference.trim());
      if (paidAt) fd.append("paid_at", paidAt);
      if (proof) fd.append("proof_of_payment", proof);
    }

    try {
      const res: any = await createGuest({ eventId, body: fd }).unwrap();
      setOk(res?.message || "Guest card issued.");
      setTimeout(onClose, 900);
    } catch (e) { setError(extractError(e)); }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-gray-900">Issue Guest Cards</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-500" /></button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <p className="text-[11px] text-gray-400 font-medium">
            A guest attends on a member's account. They get no registration
            number, no attendance and no CPD — for someone attending in their own
            right, register them instead. A member bringing more than one guest
            pays for them all at once, and each guest still gets their own card.
          </p>

          {/* Whose guest they are, FIRST and required — a guest attends on a
              member's account, and a card naming nobody cannot be placed at the
              gate. The member is found by membership number; their name and
              email are then filled in from the register and left read-only,
              because a host typed by hand is a host nobody can look up. */}
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              Guest of — membership number <span className="text-rose-500">*</span>
            </label>
            {host ? (
              <div className="mt-1 space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    value={host.membership_no ?? ""}
                    readOnly
                    title="From the membership register"
                    className="border rounded-lg px-3 py-2.5 text-sm bg-gray-50 text-gray-600 font-mono"
                  />
                  <input
                    value={host.name ?? ""}
                    readOnly
                    title="From the membership register"
                    className="border rounded-lg px-3 py-2.5 text-sm bg-gray-50 text-gray-600"
                  />
                  <input
                    value={host.email ?? ""}
                    readOnly
                    title="From the membership register"
                    className="sm:col-span-2 border rounded-lg px-3 py-2.5 text-sm bg-gray-50 text-gray-600"
                  />
                </div>
                <button onClick={() => setHost(null)} className="text-xs text-indigo-600 hover:underline">
                  Change member
                </button>
              </div>
            ) : (
              <>
                <input
                  value={hostQuery}
                  onChange={(e) => { setHostQuery(e.target.value); if (e.target.value.trim().length >= 2) searchMembers(e.target.value.trim()); }}
                  placeholder="Type the membership number…"
                  className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100 ${
                    error && !host ? "border-rose-300" : ""
                  }`}
                />
                {searching && <div className="text-[11px] text-gray-400 mt-1">Searching…</div>}
                {(memberResults as any[]).length > 0 && (
                  <div className="mt-1 border rounded-lg max-h-44 overflow-y-auto divide-y">
                    {(memberResults as any[]).map((m) => (
                      <button key={m.user_id} onClick={() => { setHost(m); setHostQuery(""); }} className="w-full text-left px-3 py-2 hover:bg-gray-50">
                        <div className="text-sm font-semibold text-gray-900">
                          {m.membership_no || "— no membership no —"}
                        </div>
                        <div className="text-[11px] text-gray-400">{m.name}{m.email ? ` · ${m.email}` : ""}</div>
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-[11px] text-gray-400 font-medium mt-1">
                  The member's name and email are filled in from the register once
                  you pick them — they are not typed here.
                </p>
              </>
            )}
          </div>

          {/* The guests' own details — theirs, not the member's, so these are
              the only name fields anybody types. One block per guest: a member
              paying for two is still admitting two people, each with their own
              card and their own row on the door list. */}
          <div className="border-t pt-4">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              Guests <span className="text-rose-500">*</span>
            </label>

            <div className="mt-1 space-y-3">
              {rows.map((row, i) => (
                <div key={i} className="rounded-xl border border-gray-100 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Guest {i + 1}
                    </span>
                    {rows.length > 1 && (
                      <button
                        onClick={() => removeRow(i)}
                        title="Remove this guest"
                        className="p-1 rounded-lg text-gray-300 hover:text-rose-600 hover:bg-rose-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input value={row.name} onChange={(e) => setRow(i, { name: e.target.value })} placeholder="Guest's full name (surname first)" className="sm:col-span-2 border rounded-lg px-3 py-2.5 text-sm" />
                    <input value={row.email} onChange={(e) => setRow(i, { email: e.target.value })} type="email" placeholder="Email (optional)" className="border rounded-lg px-3 py-2.5 text-sm" />
                    <input value={row.phone} onChange={(e) => setRow(i, { phone: e.target.value })} type="tel" placeholder="Phone no. (optional)" className="border rounded-lg px-3 py-2.5 text-sm" />
                  </div>
                </div>
              ))}
            </div>

            {rows.length < 10 && (
              <button
                onClick={addRow}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600 hover:underline"
              >
                <Plus className="w-3.5 h-3.5" /> Add another guest
              </button>
            )}
          </div>

          <div className="space-y-3 border-t pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Fee per guest (₦)</label>
                <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0 — complimentary" className="mt-1 w-full border rounded-lg px-3 py-2.5 text-sm" />
                {/* Spelled out because the member pays the total in one go and
                    the desk has to know what to collect. */}
                {settling && (
                  <p className="text-[10px] text-gray-500 font-bold mt-1">
                    {Math.max(named.length, 1)} × {naira(amount)} = {naira(total)} in one payment
                  </p>
                )}
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Method</label>
                <select value={method} onChange={(e) => setMethod(e.target.value)} disabled={!settling} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-400">
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="pos">POS</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                  Paid into (bank account) {settling && <span className="text-rose-500">*</span>}
                </label>
                <select
                  value={bankAccountId}
                  onChange={(e) => setBankAccountId(e.target.value)}
                  disabled={!settling}
                  className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-400 ${
                    settling && !bankAccountId ? "border-rose-300" : ""
                  }`}
                >
                  <option value="">— Select account —</option>
                  {(bankAccounts as any[]).map((acc: any) => (
                    <option key={acc.id} value={acc.id}>
                      {[acc.bank_name, acc.account_name ?? acc.name, acc.account_number].filter(Boolean).join(" — ")}
                    </option>
                  ))}
                </select>
                {settling && (
                  <p className="text-[10px] text-gray-400 font-medium mt-1">
                    The payment is receipted and posted to the ledger against this account.
                  </p>
                )}
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Reference</label>
                <input value={reference} onChange={(e) => setReference(e.target.value)} disabled={!settling} placeholder="Teller / transfer ref" className="mt-1 w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" />
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Paid on</label>
                <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} disabled={!settling} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-50" />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Proof of payment (optional)</label>
              <input type="file" accept="image/png,image/jpeg,application/pdf" disabled={!settling} onChange={(e) => setProof(e.target.files?.[0] ?? null)} className="mt-1 text-xs" />
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={markPaid} onChange={(e) => setMarkPaid(e.target.checked)} /> Fee collected
            </label>
            {!settling && (
              <p className="text-[11px] text-gray-400 font-medium">
                No fee recorded — the cards are issued complimentary and nothing is billed.
              </p>
            )}
          </div>

          {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{error}</div>}
          {ok && <div className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">{ok}</div>}
        </div>

        <div className="flex gap-3 px-4 sm:px-6 pb-6 pt-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="flex-1 py-3 border rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={saving} className="flex-1 py-3 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-60 flex items-center justify-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {settling
              ? `Issue & Take ${naira(total)}`
              : `Issue ${Math.max(named.length, 1)} Card${named.length > 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Add Registration / Record Bank Payment Modal ──────────────────────────────
function AddRegistrationModal({ eventId, onClose }: { eventId: string; onClose: () => void }) {
  const { data: event } = useAdminGetEventQuery(eventId);
  const [searchMembers, { data: memberResults = [], isFetching: searching }] = useLazyAdminSearchEventMembersQuery();
  const [createReg, { isLoading: saving }] = useAdminCreateRegistrationMutation();

  const tiers: any[] = event?.category_prices ?? [];
  const isPaid = !!event?.is_paid;

  const [mode, setMode] = useState<"member" | "guest">("member");
  const [memberQuery, setMemberQuery] = useState("");
  const [selected, setSelected] = useState<any | null>(null);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [categoryKey, setCategoryKey] = useState("");
  // Proof of the category, for the tiers that ask for one. Never blocked at the
  // desk — the delegate is standing there with the card in their hand — but
  // captured when it can be, so the roll can be checked afterwards.
  const [idCard, setIdCard] = useState<File | null>(null);
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [method, setMethod] = useState("bank_transfer");
  const [bankAccountId, setBankAccountId] = useState("");
  const { data: bankAccounts = [] } = useGetLedgerAccountsQuery();
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [markPaid, setMarkPaid] = useState(true);
  const [proof, setProof] = useState<File | null>(null);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  // Default the amount from the chosen tier (or flat price).
  //
  // `current_amount` is the tier's fee under the window in force today, which
  // is what the desk is taking now; a fee being entered late is corrected in
  // the Amount field, which is what it is there for.
  const tierAmount = (key: string) => {
    const t = tiers.find((x) => x.category_key === key);
    return t ? (t.current_amount ?? t.amount) : undefined;
  };
  const selectedTier = tiers.find((t) => t.category_key === categoryKey) ?? null;

  const onTier = (key: string) => {
    setCategoryKey(key);
    setIdCard(null);
    const a = key ? tierAmount(key) : event?.price;
    if (a != null) setAmount(String(a));
  };

  const submit = async () => {
    setError(""); setOk("");
    if (mode === "member" && !selected) { setError("Select a member."); return; }
    if (mode === "guest" && (!guestName.trim() || !guestEmail.trim())) { setError("Enter the attendee's name and email."); return; }
    // The ledger credits this account — without it the money can't be
    // reconciled against the bank it was actually paid into.
    if (isPaid && markPaid && !bankAccountId) { setError("Select the bank account this was paid into."); return; }

    const fd = new FormData();
    if (mode === "member") fd.append("user_id", String(selected.user_id));
    else {
      fd.append("attendee_name", guestName.trim());
      fd.append("attendee_email", guestEmail.trim());
      if (guestPhone.trim()) fd.append("attendee_phone", guestPhone.trim());
    }
    if (categoryKey) fd.append("category_key", categoryKey);
    if (idCard) fd.append("id_card", idCard);
    if (isPaid) {
      if (amount !== "") fd.append("amount", amount);
      fd.append("mark_paid", markPaid ? "1" : "0");
      if (reference.trim()) fd.append("payment_reference", reference.trim());
      fd.append("payment_method", method);
      if (bankAccountId) fd.append("bank_account_id", bankAccountId);
      if (paidAt) fd.append("paid_at", paidAt);
      if (proof) fd.append("proof_of_payment", proof);
    }
    try {
      const res: any = await createReg({ eventId, body: fd }).unwrap();
      setOk(res?.message || "Registered.");
      setTimeout(onClose, 900);
    } catch (e) { setError(extractError(e)); }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-gray-900">Register Attendee {isPaid && "/ Record Payment"}</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-500" /></button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          {/* Mode toggle */}
          <div className="flex gap-2">
            {(["member", "guest"] as const).map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); setError(""); }}
                className={`flex-1 py-2 rounded-lg text-xs font-bold uppercase tracking-widest border ${mode === m ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-500 border-gray-200"}`}
              >
                {m === "member" ? "Existing Member" : "Guest"}
              </button>
            ))}
          </div>

          {mode === "member" ? (
            <div>
              {selected ? (
                <div className="flex items-center justify-between bg-gray-50 border rounded-lg px-3 py-2">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{selected.name}</div>
                    <div className="text-[11px] text-gray-400">{selected.email}{selected.membership_no ? ` · ${selected.membership_no}` : ""}</div>
                  </div>
                  <button onClick={() => setSelected(null)} className="text-xs text-indigo-600 hover:underline">Change</button>
                </div>
              ) : (
                <>
                  <input
                    value={memberQuery}
                    onChange={(e) => { setMemberQuery(e.target.value); if (e.target.value.trim().length >= 2) searchMembers(e.target.value.trim()); }}
                    placeholder="Search member by name, email or membership no…"
                    className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
                  />
                  {searching && <div className="text-[11px] text-gray-400 mt-1">Searching…</div>}
                  {(memberResults as any[]).length > 0 && (
                    <div className="mt-1 border rounded-lg max-h-44 overflow-y-auto divide-y">
                      {(memberResults as any[]).map((m) => (
                        <button key={m.user_id} onClick={() => { setSelected(m); setMemberQuery(""); }} className="w-full text-left px-3 py-2 hover:bg-gray-50">
                          <div className="text-sm font-semibold text-gray-900">{m.name}</div>
                          <div className="text-[11px] text-gray-400">{m.email}{m.membership_no ? ` · ${m.membership_no}` : ""}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Full name (surname first)" className="sm:col-span-2 border rounded-lg px-3 py-2.5 text-sm" />
              <input value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} type="email" placeholder="Email" className="border rounded-lg px-3 py-2.5 text-sm" />
              <input value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} type="tel" placeholder="Phone no." className="border rounded-lg px-3 py-2.5 text-sm" />
              <p className="sm:col-span-2 text-[11px] text-gray-400 -mt-1">For non‑members, use the details from their registration slip.</p>
            </div>
          )}

          {/* Pricing + payment (paid events) */}
          {isPaid && (
            <div className="space-y-3 border-t pt-4">
              {tiers.length > 0 && (
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Category</label>
                  <select value={categoryKey} onChange={(e) => onTier(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white">
                    <option value="">{event?.price != null ? `Default — ${naira(event.price)}` : "Select…"}</option>
                    {tiers.map((t) => (
                      <option key={t.category_key} value={t.category_key}>
                        {t.label} — {naira(t.current_amount ?? t.amount)}
                      </option>
                    ))}
                  </select>
                  {selectedTier?.requires_id_card && (
                    <div className="mt-2">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        ID card for {selectedTier.label}
                      </label>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,application/pdf"
                        onChange={(e) => setIdCard(e.target.files?.[0] ?? null)}
                        className="mt-1 text-xs"
                      />
                      <p className="text-[10px] text-gray-400 mt-1">
                        This rate is only open to those who can show it. Attach the card if you have
                        it — the registration is saved either way.
                      </p>
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Amount (₦)</label>
                  <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={String(event?.price ?? "0")} className="mt-1 w-full border rounded-lg px-3 py-2.5 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Method</label>
                  <select value={method} onChange={(e) => setMethod(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white">
                    {/* Money that arrived outside the app is a bank transfer —
                        the account below records which bank it landed in. There
                        is no separate "deposit": paying into the account IS the
                        transfer. Cash is the one genuinely different case. */}
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cash">Cash</option>
                    <option value="pos">POS</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Paid into (bank account) {markPaid && <span className="text-rose-500">*</span>}
                  </label>
                  <select
                    value={bankAccountId}
                    onChange={(e) => setBankAccountId(e.target.value)}
                    className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white ${
                      markPaid && !bankAccountId ? "border-rose-300" : ""
                    }`}
                  >
                    <option value="">— Select account —</option>
                    {(bankAccounts as any[]).map((acc: any) => (
                      <option key={acc.id} value={acc.id}>
                        {[acc.bank_name, acc.account_name ?? acc.name, acc.account_number].filter(Boolean).join(" — ")}
                      </option>
                    ))}
                  </select>
                  {markPaid && (
                    <p className="text-[10px] text-gray-400 font-medium mt-1">
                      The payment is posted to the ledger against this account.
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Reference</label>
                  <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Teller / transfer ref" className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Paid on</label>
                  <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Proof of payment (optional)</label>
                <input type="file" accept="image/png,image/jpeg,application/pdf" onChange={(e) => setProof(e.target.files?.[0] ?? null)} className="mt-1 text-xs" />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={markPaid} onChange={(e) => setMarkPaid(e.target.checked)} /> Mark as paid
              </label>
            </div>
          )}

          {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{error}</div>}
          {ok && <div className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">{ok}</div>}
        </div>

        {/* Stuck to the bottom of the sheet on a phone, so the primary action
            is reachable without scrolling back down a long form. */}
        <div className="flex gap-3 px-4 sm:px-6 pb-6 pt-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="flex-1 py-3 border rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={saving} className="flex-1 py-3 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 flex items-center justify-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {isPaid ? "Register & Save Payment" : "Register"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Bulk Registration Upload (Excel / CSV) ────────────────────────────────────
/**
 * A chapter mails in a spreadsheet of delegates; this registers all of them.
 *
 * Two steps on purpose. The file is CHECKED first (a dry run that writes
 * nothing) and the verdict for every row is shown — who is a member, who is a
 * guest, what each will be charged, and which rows cannot be imported and why.
 * Only then does the Import button appear. Uploading two hundred fees straight
 * into the ledger on one click, with a typo in the amount column, is not a
 * mistake anyone can unwind from this screen.
 *
 * The bank account is asked for HERE rather than in the sheet: it is what the
 * ledger credits, and a column of account ids typed into Excel is the quickest
 * way to misattribute an event's whole takings.
 */
function ImportRegistrationsModal({
  eventId, onClose, onImported,
}: { eventId: string; onClose: () => void; onImported: () => void }) {
  const { data: event } = useAdminGetEventQuery(eventId);
  const [preview, { isLoading: checking }] = useAdminPreviewRegistrationImportMutation();
  const [runImport, { isLoading: importing }] = useAdminImportRegistrationsMutation();
  const { data: bankAccounts = [] } = useGetLedgerAccountsQuery();

  const isPaid = !!event?.is_paid;
  const tiers: any[] = event?.category_prices ?? [];

  const [file, setFile] = useState<File | null>(null);
  const [markPaid, setMarkPaid] = useState(true);
  const [method, setMethod] = useState("bank_transfer");
  const [bankAccountId, setBankAccountId] = useState("");
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState<any | null>(null);
  const [showFormat, setShowFormat] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [downloading, setDownloading] = useState(false);

  // The dry run's verdict is only about the file AS SENT — change the file or
  // any of the settings that ride with it and it no longer describes what would
  // happen, so it is thrown away and has to be re-checked.
  const invalidate = () => { setResult(null); setError(""); setOk(""); };

  const body = () => {
    const fd = new FormData();
    fd.append("file", file as File);
    if (isPaid) {
      fd.append("mark_paid", markPaid ? "1" : "0");
      fd.append("payment_method", method);
      if (bankAccountId) fd.append("bank_account_id", bankAccountId);
      if (paidAt) fd.append("paid_at", paidAt);
    }
    return fd;
  };

  const check = async () => {
    setError(""); setOk("");
    if (!file) { setError("Choose the spreadsheet to upload."); return; }
    // Mirrors the server's rule: without the account the ledger cannot be told
    // where the money landed.
    if (isPaid && markPaid && !bankAccountId) { setError("Select the bank account this money was paid into."); return; }

    try {
      const res: any = await preview({ eventId, body: body() }).unwrap();
      setResult(res?.data ?? res);
    } catch (e: any) {
      setError(extractError(e));
      // A capacity refusal still carries the row-by-row verdict — showing it
      // beats a one-line error the desk cannot act on.
      setResult(e?.data?.data ?? null);
    }
  };

  const commit = async () => {
    setError(""); setOk("");
    try {
      const res: any = await runImport({ eventId, body: body() }).unwrap();
      setResult(res?.data ?? res);
      setOk(res?.message || "Imported.");
      onImported();
    } catch (e: any) {
      setError(extractError(e));
    }
  };

  const downloadTemplate = async () => {
    setDownloading(true);
    setError("");
    try {
      // RTK Query parses JSON, so the workbook is fetched by hand with the
      // token attached — the same way the report export downloads.
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}/admin/events/${eventId}/registrations/import/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "event-registrations-template.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.message ?? "Could not download the template.");
    } finally {
      setDownloading(false);
    }
  };

  const rows: any[] = result?.rows ?? [];
  const imported = Number(result?.imported ?? 0) > 0;

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-gray-900">Upload Registrations</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-500" /></button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          {/* Template + format */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <button
              onClick={downloadTemplate}
              disabled={downloading}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700 text-[10px] font-black uppercase tracking-widest hover:bg-indigo-100 disabled:opacity-60"
            >
              {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              Download Excel template
            </button>
            <button
              onClick={() => setShowFormat((s) => !s)}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-gray-500 text-[10px] font-black uppercase tracking-widest hover:text-gray-700"
            >
              <FileText className="w-3.5 h-3.5" /> {showFormat ? "Hide" : "Show"} column format
            </button>
          </div>

          {showFormat && <ImportFormatHelp isPaid={isPaid} tiers={tiers} hasRefreshment={!!event?.has_refreshment} />}

          {/* File */}
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Spreadsheet (.xlsx or .csv)</label>
            <input
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); invalidate(); }}
              className="mt-1 block w-full text-xs"
            />
            <p className="text-[10px] text-gray-400 font-medium mt-1">
              The first row must be the column headings. Columns may appear in any order, and extra columns are ignored.
            </p>
          </div>

          {/* Payment settings — applied to every row that does not say otherwise */}
          {isPaid && (
            <div className="space-y-3 border-t pt-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={markPaid} onChange={(e) => { setMarkPaid(e.target.checked); invalidate(); }} />
                Mark these registrations as paid
              </label>
              <p className="text-[11px] text-gray-400 -mt-1">
                A <code className="font-mono">paid</code> column in the sheet overrides this per row, so one upload can
                carry both settled delegates and those still owing.
              </p>

              {markPaid && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Method</label>
                    <select
                      value={method}
                      onChange={(e) => { setMethod(e.target.value); invalidate(); }}
                      className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white"
                    >
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="cash">Cash</option>
                      <option value="pos">POS</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Paid into (bank account) <span className="text-rose-500">*</span>
                    </label>
                    <select
                      value={bankAccountId}
                      onChange={(e) => { setBankAccountId(e.target.value); invalidate(); }}
                      className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white ${bankAccountId ? "" : "border-rose-300"}`}
                    >
                      <option value="">— Select account —</option>
                      {(bankAccounts as any[]).map((acc: any) => (
                        <option key={acc.id} value={acc.id}>
                          {[acc.bank_name, acc.account_name ?? acc.name, acc.account_number].filter(Boolean).join(" — ")}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Paid on</label>
                    <input
                      type="date"
                      value={paidAt}
                      onChange={(e) => { setPaidAt(e.target.value); invalidate(); }}
                      className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Verdict */}
          {result && (
            <div className="border-t pt-4 space-y-3">
              <div className="flex flex-wrap gap-2">
                <Stat label={imported ? "Imported" : "Ready"} value={imported ? result.imported : result.valid} tone="emerald" />
                <Stat label={imported ? "Skipped" : "Cannot import"} value={result.invalid} tone={result.invalid ? "rose" : "gray"} />
                <Stat label="Rows read" value={result.total_rows} tone="gray" />
                {isPaid && <Stat label={imported ? "Recorded" : "To be charged"} value={naira(result.to_charge)} tone="sky" />}
              </div>

              <div className="border rounded-xl overflow-hidden">
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        <th className="text-left px-3 py-2">Row</th>
                        <th className="text-left px-3 py-2">Attendee</th>
                        <th className="text-left px-3 py-2">Type</th>
                        {isPaid && <th className="text-right px-3 py-2">Amount</th>}
                        <th className="text-left px-3 py-2">Verdict</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {rows.map((r: any) => (
                        <tr key={r.line} className={r.status === "error" ? "bg-rose-50/50" : ""}>
                          <td className="px-3 py-2 text-gray-400 font-mono text-xs">{r.line}</td>
                          <td className="px-3 py-2">
                            <div className="font-semibold text-gray-900">{r.name || "—"}</div>
                            <div className="text-[11px] text-gray-400">
                              {[r.membership_no, r.email].filter(Boolean).join(" · ") || "—"}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500 capitalize">{r.type}</td>
                          {isPaid && (
                            <td className="px-3 py-2 text-right text-xs text-gray-700 whitespace-nowrap">
                              {r.amount != null ? naira(r.amount) : "—"}
                              {r.mark_paid
                                ? <span className="block text-[10px] text-emerald-600 font-bold uppercase">paid</span>
                                : <span className="block text-[10px] text-amber-600 font-bold uppercase">owing</span>}
                            </td>
                          )}
                          <td className="px-3 py-2">
                            {r.status === "ok" ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600">
                                <CheckCircle2 className="w-3.5 h-3.5" /> {imported ? "Imported" : "Ready"}
                              </span>
                            ) : (
                              <ul className="text-[11px] text-rose-600 space-y-0.5">
                                {r.errors.map((msg: string, i: number) => <li key={i}>{msg}</li>)}
                              </ul>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {!imported && result.invalid > 0 && (
                <p className="text-[11px] text-gray-500 font-medium">
                  The {result.invalid} row(s) above will be skipped. Fix them in the sheet and upload again if they
                  should be registered.
                </p>
              )}
            </div>
          )}

          {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{error}</div>}
          {ok && <div className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">{ok}</div>}
        </div>

        <div className="flex gap-3 px-4 sm:px-6 pb-6 pt-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="flex-1 py-3 border rounded-lg text-sm hover:bg-gray-50">
            {imported ? "Done" : "Cancel"}
          </button>
          {!imported && (
            <button
              onClick={result && result.valid > 0 ? commit : check}
              disabled={checking || importing || !file}
              className="flex-1 py-3 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {(checking || importing) && <Loader2 className="w-4 h-4 animate-spin" />}
              {result && result.valid > 0 ? `Import ${result.valid} registration(s)` : "Check file"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** The column reference, shown next to the upload rather than buried in a manual. */
function ImportFormatHelp({
  isPaid, tiers, hasRefreshment,
}: { isPaid: boolean; tiers: any[]; hasRefreshment: boolean }) {
  const cols: [string, string, string][] = [
    ["membership_no", "For members", "Their membership number, e.g. F/06970. Everything else about them is read off their record, so name/email can be left blank."],
    ["name", "For guests", "Surname first. Required when the row is not a member."],
    ["email", "For guests", "Required for a guest. If it matches a member's account, the row registers that member instead."],
    ["phone", "Optional", "Falls back to the member's own number."],
    ["category_key", isPaid ? "Optional" : "Ignored", tiers.length
      ? `One of: ${tiers.map((t) => t.category_key).join(", ")}. Left blank, a member gets the tier for their grade — the seat in the hall, so a virtual delegate must be given their _virtual key here. A tier on the fee timeline is charged at the fee in force on the row's paid_at date.`
      : "This event has no price tiers, so leave it blank."],
    ["amount", isPaid ? "Optional" : "Ignored", "Overrides the tier price for that row. ₦ and commas are fine."],
    ["paid", isPaid ? "Optional" : "Ignored", "yes / no. Blank falls back to the setting on this form."],
    ["payment_method", isPaid ? "Optional" : "Ignored", "bank transfer / cash / pos. Blank falls back to the setting on this form."],
    ["payment_reference", "Optional", "Teller or transfer reference. Must not repeat one already used."],
    ["paid_at", "Optional", "Date the money was received, e.g. 2026-09-01."],
    ["refreshment_mode", hasRefreshment ? "Optional" : "Ignored", hasRefreshment ? "takeaway or buffet." : "This event serves no refreshments."],
  ];

  return (
    <div className="border rounded-xl bg-gray-50/60 p-3 space-y-2">
      <p className="text-[11px] text-gray-500 font-medium">
        Put the headings in row 1 — any order, extra columns ignored. A row identifies either a
        <strong> member</strong> (membership_no) or a <strong>guest</strong> (name + email). The
        downloadable template carries this same table on its <em>Column format</em> sheet, so it
        travels with the file when it is mailed out to be filled in.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <tbody className="divide-y divide-gray-200">
            {cols.map(([name, req, note]) => (
              <tr key={name}>
                <td className="py-1.5 pr-3 font-mono font-semibold text-gray-800 whitespace-nowrap align-top">{name}</td>
                <td className="py-1.5 pr-3 text-gray-400 whitespace-nowrap align-top">{req}</td>
                <td className="py-1.5 text-gray-600">{note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Record Offline Payment (for an existing pending registration) ─────────────
function RecordPaymentModal({ eventId, reg, onClose }: { eventId: string; reg: any; onClose: () => void }) {
  const [recordPayment, { isLoading: saving }] = useAdminRecordRegistrationPaymentMutation();

  const [amount, setAmount] = useState(reg.amount_paid != null ? String(reg.amount_paid) : "");
  // Existing rows may still carry a retired method ('bank_deposit'), which is no
  // longer an option — fall back rather than leaving the select on a blank value.
  const [method, setMethod] = useState(
    ["bank_transfer", "cash", "pos"].includes(reg.payment_method) ? reg.payment_method : "bank_transfer",
  );
  const [bankAccountId, setBankAccountId] = useState(reg.bank_account_id ? String(reg.bank_account_id) : "");
  const [reference, setReference] = useState(reg.payment_reference || "");
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [proof, setProof] = useState<File | null>(null);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const { data: bankAccounts = [] } = useGetLedgerAccountsQuery();

  const submit = async () => {
    setError(""); setOk("");
    // Mandatory: the ledger entry is credited to this account.
    if (!bankAccountId) { setError("Select the bank account this was paid into."); return; }

    const fd = new FormData();
    if (amount !== "") fd.append("amount", amount);
    fd.append("payment_method", method);
    fd.append("bank_account_id", bankAccountId);
    if (reference.trim()) fd.append("payment_reference", reference.trim());
    if (paidAt) fd.append("paid_at", paidAt);
    if (proof) fd.append("proof_of_payment", proof);
    try {
      const res: any = await recordPayment({ eventId, registrationId: reg.id, body: fd }).unwrap();
      setOk(res?.message || "Payment recorded.");
      setTimeout(onClose, 900);
    } catch (e) { setError(extractError(e)); }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] sm:max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-gray-900">Record Offline Payment</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-500" /></button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <div className="bg-gray-50 border rounded-lg px-3 py-2">
            <div className="text-sm font-semibold text-gray-900">{reg.attendee_name ?? reg.user?.name ?? "—"}</div>
            <div className="text-[11px] text-gray-400 break-words">{reg.registration_no}{reg.attendee_email ? ` · ${reg.attendee_email}` : ""}</div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Amount (₦)</label>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" className="mt-1 w-full border rounded-lg px-3 py-2.5 text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Method</label>
              <select value={method} onChange={(e) => setMethod(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white">
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cash">Cash</option>
                <option value="pos">POS</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                Paid into (bank account) <span className="text-rose-500">*</span>
              </label>
              <select
                value={bankAccountId}
                onChange={(e) => setBankAccountId(e.target.value)}
                className={`mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white ${
                  bankAccountId ? "" : "border-rose-300"
                }`}
              >
                <option value="">— Select account —</option>
                {(bankAccounts as any[]).map((acc: any) => (
                  <option key={acc.id} value={acc.id}>
                    {[acc.bank_name, acc.account_name ?? acc.name, acc.account_number].filter(Boolean).join(" — ")}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Paid on</label>
              <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Reference</label>
              <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Teller / transfer ref" className="mt-1 w-full border rounded-lg px-3 py-2.5 text-sm" />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Proof of payment (optional)</label>
            <input type="file" accept="image/png,image/jpeg,application/pdf" onChange={(e) => setProof(e.target.files?.[0] ?? null)} className="mt-1 text-xs" />
          </div>

          {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{error}</div>}
          {ok && <div className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2">{ok}</div>}
        </div>

        <div className="flex gap-3 px-4 sm:px-6 pb-6 pt-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="flex-1 py-3 border rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={saving} className="flex-1 py-3 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 flex items-center justify-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Mark Paid
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The materials desk: hand packs to attendees.
 *
 * Materials could already be issued, but only from inside one registrant's row
 * on the Registrations tab, under its third sub-tab — three clicks deep, and it
 * refused outright unless the registration carried a barcode. That is the door's
 * workflow, not the materials table's. Whoever is handing out packs works from a
 * name or a membership number in front of them, and wants the next attendee
 * immediately after.
 *
 * The collected state is read from the registrations list, which already
 * carries each attendee's materials, rather than from the scanner lookup the
 * registrant row uses — that lookup needs event.scanner.use, which the person
 * on this table has no reason to hold.
 */
function MaterialsIssueDesk({ eventId, materials }: { eventId: string; materials: any[] }) {
  const { data: regs = [], isLoading } = useAdminGetEventRegistrationsQuery(eventId);
  const [issueMaterials, { isLoading: issuing }] = useAdminIssueRegistrationMaterialsMutation();

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<any | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // The row is re-read from the list on every render, so it refreshes in place
  // when the mutation invalidates the registrations tag — no local copy to go
  // stale, and the desk sees the pack it just handed over marked issued.
  const current = selected
    ? (regs as any[]).find((r) => r.id === selected.id) ?? selected
    : null;

  // Only what the desk can act on. An unpaid delegate is refused by the API, so
  // showing them here would just be an error waiting to happen — they are sent
  // to the registrations tab to have the payment recorded first.
  const matches = search.trim()
    ? (regs as any[]).filter((r) => registrationMatches(r, search)).slice(0, 12)
    : [];

  const collectedIds: number[] = ((current?.materials ?? []) as any[])
    .filter((m: any) => m.is_collected)
    .map((m: any) => m.material_id);

  const settled = current ? ["paid", "free"].includes(current.payment_status) : false;
  const outstanding = materials.filter((m: any) => !collectedIds.includes(m.id));

  const issue = async (ids: number[]) => {
    if (!current || ids.length === 0) return;
    setBusyId(ids.length === 1 ? ids[0] : -1);
    setMsg(null);
    try {
      const res: any = await issueMaterials({
        eventId,
        registrationId: current.id,
        material_updates: ids.map((id) => ({ event_material_id: id, is_collected: true })),
      }).unwrap();
      setMsg({ ok: true, text: res?.message ?? "Issued." });
    } catch (e) {
      setMsg({ ok: false, text: extractError(e) });
    }
    setBusyId(null);
  };

  const pick = (r: any) => {
    setSelected(r);
    setSearch("");
    setMsg(null);
  };

  if (materials.length === 0) return null;

  return (
    <div className="mb-6 rounded-2xl border border-indigo-100 bg-indigo-50/40 p-4 sm:p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Package className="w-4 h-4 text-indigo-500 shrink-0" />
        <div className="text-[10px] font-black text-indigo-700 uppercase tracking-widest">
          Issue materials to an attendee
        </div>
      </div>

      {/* Find the attendee */}
      <div className="relative">
        <Search className="w-3.5 h-3.5 text-gray-300 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={isLoading ? "Loading delegates…" : "Name, membership no, email or reg no…"}
          disabled={isLoading}
          className="w-full pl-8 pr-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-bold text-gray-700 placeholder-gray-300 outline-none focus:border-indigo-400"
        />

        {search.trim() && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-lg max-h-64 overflow-y-auto divide-y divide-gray-50">
            {matches.length === 0 && (
              <p className="px-3 py-3 text-xs font-medium text-gray-400">No delegate matches that.</p>
            )}
            {matches.map((r: any) => (
              <button
                key={r.id}
                onClick={() => pick(r)}
                className="w-full text-left px-3 py-2.5 hover:bg-indigo-50 transition-colors"
              >
                <div className="text-sm font-bold text-gray-800 truncate">
                  {r.attendee_name ?? r.user?.name ?? "—"}
                </div>
                <div className="text-[10px] font-bold text-gray-400 truncate">
                  {r.registration_no}
                  {r.user?.member?.membership_no ? ` · ${r.user.member.membership_no}` : ""}
                  {["paid", "free"].includes(r.payment_status) ? "" : " · UNPAID"}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* The chosen attendee and their pack */}
      {current && (
        <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-black text-gray-900 text-sm truncate">
                {current.attendee_name ?? current.user?.name ?? "—"}
              </div>
              <div className="text-[10px] font-bold text-gray-400 truncate">
                {current.registration_no}
                {current.user?.member?.membership_no ? ` · ${current.user.member.membership_no}` : ""}
              </div>
            </div>
            <button
              onClick={() => { setSelected(null); setMsg(null); }}
              className="shrink-0 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-700"
            >
              Change
            </button>
          </div>

          {msg && (
            <div className={`text-xs font-bold px-3 py-2 rounded-xl border ${
              msg.ok
                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                : "bg-rose-50 text-rose-700 border-rose-100"
            }`}>
              {msg.text}
            </div>
          )}

          {/* An unsettled fee is refused by the API, so it is said here rather
              than discovered by pressing Issue. */}
          {!settled && (
            <div className="flex items-start gap-2 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              This delegate's fee is outstanding. Record the payment on the
              Registrations tab before issuing materials.
            </div>
          )}

          <div className="space-y-1.5">
            {materials.map((m: any) => {
              const already = collectedIds.includes(m.id);
              const depleted = m.quantity_total != null && m.quantity_remaining === 0;
              const busy = busyId === m.id || busyId === -1;
              return (
                <div
                  key={m.id}
                  className={`flex items-center justify-between gap-3 p-2.5 rounded-xl border transition-colors ${
                    already ? "bg-emerald-50 border-emerald-100" : "bg-gray-50 border-transparent"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-bold flex items-center gap-1.5 ${already ? "text-emerald-800" : "text-gray-700"}`}>
                      {already && <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />}
                      <span className="truncate">{m.name}</span>
                    </div>
                    {m.quantity_total != null && !already && (
                      <div className={`text-[10px] font-bold ${depleted ? "text-rose-600" : "text-gray-400"}`}>
                        {m.quantity_remaining} of {m.quantity_total} left{depleted ? " — DEPLETED" : ""}
                      </div>
                    )}
                  </div>
                  {already ? (
                    <span className="shrink-0 text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg bg-emerald-100 text-emerald-700 border border-emerald-200">
                      Issued ✓
                    </span>
                  ) : (
                    <button
                      onClick={() => issue([m.id])}
                      disabled={busy || issuing || !settled || depleted}
                      className="shrink-0 text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-40 transition-colors flex items-center gap-1"
                    >
                      {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                      Issue
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* The common case at a materials table is the whole pack at once. */}
          {outstanding.length > 1 && (
            <button
              onClick={() => issue(outstanding.filter((m: any) => !(m.quantity_total != null && m.quantity_remaining === 0)).map((m: any) => m.id))}
              disabled={issuing || !settled}
              className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white font-black text-xs py-3 rounded-xl hover:bg-gray-800 disabled:opacity-40 transition-colors"
            >
              {issuing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Package className="w-3.5 h-3.5" />}
              Issue all {outstanding.length} remaining
            </button>
          )}

          {outstanding.length === 0 && (
            <p className="text-xs font-bold text-emerald-700 text-center py-1">
              This attendee has collected everything.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Materials Panel ───────────────────────────────────────────────────────────
// ── Documents Panel ───────────────────────────────────────────────────────────
/**
 * The event's files: programme, papers, presentations, communiqué, photographs.
 *
 * A separate tab from Materials on purpose. That tab is a stock table — bags
 * and jotters, counted, issued to a named delegate at a desk and reconciled
 * afterwards. Nothing here has a quantity or can run out, and it is collected
 * by download rather than by queueing, so putting the two in one list would
 * have meant half the columns reading "—" on every row.
 *
 * Uploading and publishing are separate acts, because papers are gathered
 * before an event and released after it. An unpublished document is visible
 * here and nowhere else.
 */
const DOC_VISIBILITY: { key: string; label: string; hint: string; icon: any; tone: string }[] = [
  { key: "registered", label: "Delegates only", hint: "Only members registered for this event",  icon: Lock,   tone: "bg-amber-50 text-amber-700 border-amber-100" },
  { key: "members",    label: "All members",    hint: "Any signed-in NIQS member",               icon: Users,  tone: "bg-indigo-50 text-indigo-700 border-indigo-100" },
  { key: "public",     label: "Public",         hint: "Anyone, signed in or not",                icon: Globe,  tone: "bg-emerald-50 text-emerald-700 border-emerald-100" },
];

/** Bytes as the uploader thinks of them. 0 means the row exists but the file does not. */
function prettyBytes(n: any): string {
  const b = Number(n || 0);
  if (b <= 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

const DOC_EXT_TONE: Record<string, string> = {
  pdf: "bg-rose-50 text-rose-600",
  doc: "bg-blue-50 text-blue-600",   docx: "bg-blue-50 text-blue-600",
  ppt: "bg-orange-50 text-orange-600", pptx: "bg-orange-50 text-orange-600",
  xls: "bg-emerald-50 text-emerald-600", xlsx: "bg-emerald-50 text-emerald-600",
  zip: "bg-gray-100 text-gray-600",
  jpg: "bg-violet-50 text-violet-600", jpeg: "bg-violet-50 text-violet-600", png: "bg-violet-50 text-violet-600",
  mp3: "bg-pink-50 text-pink-600",   mp4: "bg-pink-50 text-pink-600",
};

function DocumentsPanel({ eventId }: { eventId: string }) {
  const { data: documents = [], isLoading } = useAdminGetEventDocumentsQuery(eventId);
  const [upload, { isLoading: uploading }]  = useAdminUploadEventDocumentMutation();
  const [updateDoc, { isLoading: saving }]  = useAdminUpdateEventDocumentMutation();
  const [deleteDoc]                         = useAdminDeleteEventDocumentMutation();

  const [showForm, setShowForm]     = useState(false);
  const [editTarget, setEditTarget] = useState<any | null>(null);
  const [file, setFile]             = useState<File | null>(null);
  const [form, setForm]             = useState({ title: "", description: "", visibility: "registered", is_published: true, sort_order: "" });
  const [formErr, setFormErr]       = useState("");
  const [msg, setMsg]               = useState("");
  const [confirmId, setConfirmId]   = useState<number | null>(null);

  const inputCls = "w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm";
  const busy = uploading || saving;

  const reset = () => {
    setFile(null);
    setForm({ title: "", description: "", visibility: "registered", is_published: true, sort_order: "" });
    setFormErr("");
  };

  const openUpload = () => { setEditTarget(null); reset(); setShowForm(true); setMsg(""); };
  const openEdit = (d: any) => {
    setEditTarget(d);
    setFile(null);
    setForm({
      title: d.title ?? "",
      description: d.description ?? "",
      visibility: d.visibility ?? "registered",
      is_published: !!d.is_published,
      sort_order: d.sort_order ? String(d.sort_order) : "",
    });
    setFormErr("");
    setShowForm(true);
    setMsg("");
  };

  const handleSave = async () => {
    if (!editTarget && !file) { setFormErr("Choose a file to upload."); return; }
    setFormErr("");

    const fd = new FormData();
    if (file) fd.append("file", file);
    // The title may be left blank on upload — the server falls back to the
    // file's own name, which is what whoever is putting up eight decks at once
    // would have typed anyway.
    fd.append("title", form.title.trim());
    fd.append("description", form.description.trim());
    fd.append("visibility", form.visibility);
    // Sent explicitly either way: an unticked checkbox contributes nothing to a
    // FormData, and the server would read the absence as its default of true.
    fd.append("is_published", form.is_published ? "1" : "0");
    if (form.sort_order.trim()) fd.append("sort_order", form.sort_order.trim());

    try {
      const res: any = editTarget
        ? await updateDoc({ eventId, id: editTarget.id, body: fd }).unwrap()
        : await upload({ eventId, body: fd }).unwrap();
      setMsg(res?.message ?? (editTarget ? "Document updated." : "Document uploaded."));
      setShowForm(false);
      setEditTarget(null);
      reset();
    } catch (err) {
      setFormErr(extractError(err));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteDoc({ eventId, id }).unwrap();
      setMsg("Document deleted.");
    } catch (err) {
      setMsg(extractError(err));
    }
    setConfirmId(null);
  };

  const docs = documents as any[];
  const published = docs.filter((d) => d.is_published).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
            Documents ({docs.length})
          </div>
          <div className="text-[10px] font-medium text-gray-400 mt-0.5">
            Programme, papers, presentations, communiqué · {published} published
          </div>
        </div>
        <AccessGate perm={PERMS.event.update}>
          <button
            onClick={openUpload}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest hover:bg-indigo-600 transition-colors"
          >
            <Upload className="w-3.5 h-3.5" /> Upload
          </button>
        </AccessGate>
      </div>

      {msg && (
        <div className="mb-4 flex items-center gap-2 text-xs font-bold px-3 py-2.5 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-100">
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> {msg}
          <button onClick={() => setMsg("")} className="ml-auto"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {showForm && (
        <div className="mb-5 bg-gray-50 rounded-2xl p-5 space-y-4">
          <div className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
            {editTarget ? `Edit — ${editTarget.title}` : "Upload a document"}
          </div>

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">
              {editTarget ? "Replace file (optional)" : "File *"}
            </label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.zip,.mp3,.mp4"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full text-xs font-medium text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-[10px] file:font-black file:uppercase file:tracking-widest file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
            <p className="mt-1 text-[10px] font-medium text-gray-400">
              PDF, Word, PowerPoint, Excel, images, audio, video or ZIP · up to 50 MB
              {editTarget && " · leave empty to keep the current file"}
            </p>
          </div>

          <input
            className={inputCls}
            placeholder={editTarget ? "Title" : "Title (defaults to the file name)"}
            value={form.title}
            onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          />
          <input
            className={inputCls}
            placeholder="Description — what this is, and for whom"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          />

          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Who may download it</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {DOC_VISIBILITY.map(({ key, label, hint, icon: Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, visibility: key }))}
                  className={`text-left p-3 rounded-xl border transition-colors ${
                    form.visibility === key
                      ? "bg-white border-indigo-300 ring-2 ring-indigo-100"
                      : "bg-white border-gray-100 hover:border-gray-200"
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <Icon className={`w-3.5 h-3.5 ${form.visibility === key ? "text-indigo-600" : "text-gray-300"}`} />
                    <span className={`text-[11px] font-black ${form.visibility === key ? "text-indigo-700" : "text-gray-600"}`}>{label}</span>
                  </div>
                  <div className="text-[10px] font-medium text-gray-400 mt-0.5">{hint}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_published}
                onChange={(e) => setForm((p) => ({ ...p, is_published: e.target.checked }))}
                className="w-4 h-4 rounded accent-indigo-600"
              />
              <span className="text-xs font-bold text-gray-700">Publish now</span>
              <span className="text-[10px] font-medium text-gray-400">— members see it immediately</span>
            </label>
            <div className="flex items-center gap-2 sm:ml-auto">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Order</span>
              <input
                type="number"
                min={0}
                value={form.sort_order}
                onChange={(e) => setForm((p) => ({ ...p, sort_order: e.target.value }))}
                placeholder="0"
                className="w-20 border-b-2 border-gray-100 py-1 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm"
              />
            </div>
          </div>

          {formErr && <p className="text-rose-600 text-xs font-medium">{formErr}</p>}

          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={busy}
              className="flex-1 bg-gray-900 text-white font-black text-xs py-2.5 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {editTarget ? "Save changes" : "Upload document"}
            </button>
            <button
              onClick={() => { setShowForm(false); setEditTarget(null); reset(); }}
              className="px-4 bg-gray-100 text-gray-600 font-black text-xs py-2.5 rounded-xl"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {isLoading && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-indigo-400 animate-spin" /></div>}

      {!isLoading && docs.length === 0 && !showForm && (
        <div className="text-center py-10">
          <Paperclip className="w-8 h-8 text-gray-200 mx-auto mb-3" />
          <p className="text-sm font-bold text-gray-400">No documents uploaded yet.</p>
          <p className="text-xs font-medium text-gray-300 mt-1">
            Put up the programme, the papers and the presentations here.
          </p>
        </div>
      )}

      <div className="space-y-2">
        {docs.map((d: any) => {
          const vis = DOC_VISIBILITY.find((v) => v.key === d.visibility) ?? DOC_VISIBILITY[0];
          const VisIcon = vis.icon;
          const ext = String(d.extension ?? "").toLowerCase();

          return (
            <div key={d.id} className="p-3 rounded-2xl border border-transparent bg-gray-50">
              <div className="flex items-start gap-3">
                <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-[9px] font-black uppercase ${DOC_EXT_TONE[ext] ?? "bg-gray-100 text-gray-500"}`}>
                  {ext || <FileText className="w-4 h-4" />}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-800 text-sm break-words">{d.title}</div>
                  {d.description && (
                    <div className="text-[11px] text-gray-400 font-medium break-words mt-0.5">{d.description}</div>
                  )}

                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-black px-2 py-0.5 rounded-lg border ${vis.tone}`}>
                      <VisIcon className="w-3 h-3" /> {vis.label}
                    </span>
                    {d.is_published ? (
                      <span className="text-[10px] font-black text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-lg">Published</span>
                    ) : (
                      <span className="text-[10px] font-black text-gray-500 bg-gray-200 px-2 py-0.5 rounded-lg">Draft</span>
                    )}
                    <span className="text-[10px] font-black text-gray-400 bg-gray-100 px-2 py-0.5 rounded-lg">
                      {prettyBytes(d.file_size)}
                    </span>
                    <span className="text-[10px] font-black text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-lg">
                      {d.download_count ?? 0} download{(d.download_count ?? 0) === 1 ? "" : "s"}
                    </span>
                  </div>

                  <div className="text-[10px] font-medium text-gray-300 mt-1 truncate">
                    {d.file_name}
                    {d.uploaded_by ? ` · ${d.uploaded_by}` : ""}
                    {d.uploaded_at ? ` · ${fmt(d.uploaded_at)}` : ""}
                  </div>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  {/* Straight to the file on the public disk. An admin passes
                      every visibility check anyway, so there is nothing for the
                      download route to decide — and this previews a PDF in the
                      browser instead of forcing a save. */}
                  {d.url && (
                    <a
                      href={d.url}
                      target="_blank"
                      rel="noreferrer"
                      title="Open"
                      className="p-1.5 rounded-lg bg-white text-gray-500 hover:bg-gray-100 transition-colors"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </a>
                  )}
                  <AccessGate perm={PERMS.event.update}>
                    <button onClick={() => openEdit(d)} title="Edit" className="p-1.5 rounded-lg bg-white text-gray-500 hover:bg-gray-100 transition-colors">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setConfirmId(d.id)} title="Delete" className="p-1.5 rounded-lg bg-rose-50 text-rose-500 hover:bg-rose-100 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </AccessGate>
                </div>
              </div>

              {/* Deleting takes the file off the disk with the row, so it is
                  confirmed in place rather than fired by a single click. */}
              {confirmId === d.id && (
                <div className="mt-3 flex items-center gap-2 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2.5">
                  <AlertCircle className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                  <span className="text-[11px] font-bold text-rose-700">Delete this document and its file?</span>
                  <div className="ml-auto flex gap-1.5">
                    <button onClick={() => handleDelete(d.id)} className="px-2.5 py-1 rounded-lg bg-rose-600 text-white text-[10px] font-black uppercase tracking-widest">Delete</button>
                    <button onClick={() => setConfirmId(null)} className="px-2.5 py-1 rounded-lg bg-white text-gray-600 text-[10px] font-black uppercase tracking-widest">Keep</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MaterialsPanel({ eventId }: { eventId: string }) {
  const { data: materials = [] }             = useAdminGetMaterialsQuery(eventId);
  const [createMat, { isLoading: creating }] = useAdminCreateMaterialMutation();
  const [updateMat]                          = useAdminUpdateMaterialMutation();
  const [deleteMat]                          = useAdminDeleteMaterialMutation();
  const [addStock]                           = useAdminAddMaterialStockMutation();

  const [showForm, setShowForm]         = useState(false);
  const [editTarget, setEditTarget]     = useState<any | null>(null);
  const [form, setForm]                 = useState({ name: "", description: "", quantity_total: "" });
  const [formErr, setFormErr]           = useState("");
  const [stockTarget, setStockTarget]   = useState<any | null>(null);
  const [stockQty, setStockQty]         = useState("");
  const [stockErr, setStockErr]         = useState("");
  const [addingStock, setAddingStock]   = useState(false);

  const inputCls = "w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm";

  const openCreate = () => { setEditTarget(null); setForm({ name: "", description: "", quantity_total: "" }); setFormErr(""); setShowForm(true); setStockTarget(null); };
  const openEdit   = (m: any) => { setEditTarget(m); setForm({ name: m.name, description: m.description ?? "", quantity_total: m.quantity_total ?? "" }); setFormErr(""); setShowForm(true); setStockTarget(null); };
  const openStock  = (m: any) => { setStockTarget(m); setStockQty(""); setStockErr(""); setShowForm(false); };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormErr("Name is required"); return; }
    try {
      if (editTarget) await updateMat({ eventId, id: editTarget.id, body: form }).unwrap();
      else            await createMat({ eventId, body: form }).unwrap();
      setShowForm(false);
    } catch (err) { setFormErr(extractError(err)); }
  };

  const handleAddStock = async () => {
    const qty = parseInt(stockQty, 10);
    if (!qty || qty < 1) { setStockErr("Enter a valid quantity"); return; }
    setAddingStock(true);
    try {
      await addStock({ eventId, id: stockTarget.id, quantity: qty }).unwrap();
      setStockTarget(null);
    } catch (err) { setStockErr(extractError(err)); }
    setAddingStock(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Materials ({(materials as any[]).length})</div>
        <AccessGate perm={PERMS.event.inventoryManage}>
          <button onClick={openCreate} className="flex items-center gap-1.5 text-xs font-black text-indigo-600 hover:text-indigo-700">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </AccessGate>
      </div>

      {/* Handing packs out is the job this tab exists for; defining them is
          setup done once. So the desk sits above the stock list. */}
      <AccessGate perm={PERMS.event.inventoryManage}>
        <MaterialsIssueDesk eventId={eventId} materials={materials as any[]} />
      </AccessGate>

      {showForm && (
        <div className="mb-4 bg-gray-50 rounded-2xl p-5 space-y-3">
          <input className={inputCls} placeholder="Material name *" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          <input className={inputCls} placeholder="Description" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
          <input type="number" className={inputCls} placeholder="Total quantity" value={form.quantity_total} onChange={(e) => setForm((p) => ({ ...p, quantity_total: e.target.value }))} />
          {formErr && <p className="text-rose-600 text-xs font-medium">{formErr}</p>}
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={creating} className="flex-1 bg-gray-900 text-white font-black text-xs py-2.5 rounded-xl disabled:opacity-50">{editTarget ? "Save" : "Add"}</button>
            <button onClick={() => setShowForm(false)} className="px-4 bg-gray-100 text-gray-600 font-black text-xs py-2.5 rounded-xl">Cancel</button>
          </div>
        </div>
      )}

      {stockTarget && (
        <div className="mb-4 bg-amber-50 border border-amber-100 rounded-2xl p-5 space-y-3">
          <div className="text-xs font-black text-amber-800">Add Stock — {stockTarget.name}</div>
          <div className="text-[10px] text-amber-600 font-medium">
            Current: {stockTarget.quantity_total ?? 0} total · {stockTarget.quantity_issued ?? 0} issued · {stockTarget.quantity_remaining ?? "∞"} remaining
          </div>
          <input
            type="number"
            className="w-full border-b-2 border-amber-200 py-2 font-bold text-gray-800 outline-none focus:border-amber-500 bg-transparent text-sm"
            placeholder="Units to add *"
            value={stockQty}
            onChange={(e) => setStockQty(e.target.value)}
            min={1}
          />
          {stockErr && <p className="text-rose-600 text-xs font-medium">{stockErr}</p>}
          <div className="flex gap-2">
            <button onClick={handleAddStock} disabled={addingStock} className="flex-1 bg-amber-500 text-white font-black text-xs py-2.5 rounded-xl disabled:opacity-50 flex items-center justify-center gap-1.5">
              {addingStock && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Add Stock
            </button>
            <button onClick={() => setStockTarget(null)} className="px-4 bg-gray-100 text-gray-600 font-black text-xs py-2.5 rounded-xl">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {(materials as any[]).length === 0 && !showForm && (
          <p className="text-sm text-gray-400 font-medium text-center py-4">No materials added yet.</p>
        )}
        {(materials as any[]).map((m: any) => {
          const hasQty = m.quantity_total != null;
          const depleted = hasQty && m.quantity_remaining === 0;
          return (
            <div key={m.id} className={`p-3 rounded-2xl border transition-colors ${depleted ? "bg-rose-50 border-rose-100" : "bg-gray-50 border-transparent"}`}>
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0 mr-2">
                  <div className="font-bold text-gray-800 text-sm">{m.name}</div>
                  {m.description && <div className="text-[10px] text-gray-400 font-medium">{m.description}</div>}
                  {hasQty && (
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-[10px] font-black text-gray-500 bg-gray-100 px-2 py-0.5 rounded-lg">
                        Total: {m.quantity_total}
                      </span>
                      <span className="text-[10px] font-black text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-lg">
                        Issued: {m.quantity_issued ?? 0}
                      </span>
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded-lg ${depleted ? "bg-rose-100 text-rose-700" : "bg-indigo-50 text-indigo-700"}`}>
                        Left: {m.quantity_remaining}
                      </span>
                    </div>
                  )}
                </div>
                {/* Stocking and editing are the inventory capability the API
                    demands on these endpoints — the button should not offer an
                    action that ends in a 403. */}
                <AccessGate perm={PERMS.event.inventoryManage}>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => openStock(m)} title="Add stock" className="p-1.5 rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => openEdit(m)} title="Edit" className="p-1.5 rounded-lg bg-white text-gray-500 hover:bg-gray-100 transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => deleteMat({ eventId, id: m.id })} title="Delete" className="p-1.5 rounded-lg bg-rose-50 text-rose-500 hover:bg-rose-100 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </AccessGate>
              </div>
              {depleted && (
                <div className="mt-1.5 text-[10px] font-black text-rose-600 uppercase tracking-widest">Stock depleted — add more units</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Refreshments Panel ────────────────────────────────────────────────────────
function RefreshmentsPanel({ eventId, days }: { eventId: string; days: any[] }) {
  const { data: refreshments = [] }              = useAdminGetRefreshmentsQuery(eventId);
  const [createRef, { isLoading: creating }]     = useAdminCreateRefreshmentMutation();
  const [updateRef]                              = useAdminUpdateRefreshmentMutation();
  const [deleteRef]                              = useAdminDeleteRefreshmentMutation();

  const [showForm, setShowForm]     = useState(false);
  const [editTarget, setEditTarget] = useState<any | null>(null);
  const [form, setForm]             = useState({ name: "", event_day_id: "" });
  const [formErr, setFormErr]       = useState("");

  const inputCls = "w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm";

  const openCreate = () => { setEditTarget(null); setForm({ name: "", event_day_id: "" }); setFormErr(""); setShowForm(true); };
  const openEdit   = (r: any) => { setEditTarget(r); setForm({ name: r.name, event_day_id: r.event_day_id ?? "" }); setFormErr(""); setShowForm(true); };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormErr("Name is required"); return; }
    const body: any = { name: form.name };
    if (form.event_day_id) body.event_day_id = form.event_day_id;
    try {
      if (editTarget) await updateRef({ eventId, id: editTarget.id, body }).unwrap();
      else            await createRef({ eventId, body }).unwrap();
      setShowForm(false);
    } catch (err) { setFormErr(extractError(err)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Refreshments ({(refreshments as any[]).length})</div>
        <AccessGate perm={PERMS.event.inventoryManage}>
          <button onClick={openCreate} className="flex items-center gap-1.5 text-xs font-black text-indigo-600 hover:text-indigo-700">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </AccessGate>
      </div>

      {showForm && (
        <div className="mb-4 bg-gray-50 rounded-2xl p-5 space-y-3">
          <input className={inputCls} placeholder="Refreshment name *" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          <select className={inputCls} value={form.event_day_id} onChange={(e) => setForm((p) => ({ ...p, event_day_id: e.target.value }))}>
            <option value="">All days</option>
            {days.map((d: any) => (
              <option key={d.id} value={d.id}>{d.day_label} — {fmt(d.day_date)}</option>
            ))}
          </select>
          {formErr && <p className="text-rose-600 text-xs font-medium">{formErr}</p>}
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={creating} className="flex-1 bg-gray-900 text-white font-black text-xs py-2.5 rounded-xl disabled:opacity-50">{editTarget ? "Save" : "Add"}</button>
            <button onClick={() => setShowForm(false)} className="px-4 bg-gray-100 text-gray-600 font-black text-xs py-2.5 rounded-xl">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {(refreshments as any[]).length === 0 && !showForm && (
          <p className="text-sm text-gray-400 font-medium text-center py-4">No refreshments added yet.</p>
        )}
        {(refreshments as any[]).map((r: any) => (
          <div key={r.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-2xl">
            <div>
              <div className="font-bold text-gray-800 text-sm">{r.name}</div>
              <div className="text-xs text-gray-400 font-medium">
                {r.event_day_id ? days.find((d) => d.id === r.event_day_id)?.day_label ?? `Day #${r.event_day_id}` : "All days"}
              </div>
            </div>
            <AccessGate perm={PERMS.event.inventoryManage}>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => openEdit(r)} title="Edit" className="p-1.5 rounded-lg bg-white text-gray-500 hover:bg-gray-100 transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => deleteRef({ eventId, id: r.id })} title="Delete" className="p-1.5 rounded-lg bg-rose-50 text-rose-500 hover:bg-rose-100 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </AccessGate>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── CPD Allocation Result ─────────────────────────────────────────────────────
/**
 * What the Issue CPD run did, name by name.
 *
 * The whole register is listed, not only the members who were credited: the
 * question the secretariat actually gets asked is "why did I not get my
 * points", and that is answerable only if the people who got nothing are on
 * the sheet with the reason against their name.
 *
 * The server rebuilds this from the database on every press, so pressing the
 * button again re-opens the same complete picture — everyone reading
 * "already held" instead of "issued" — rather than an empty second run.
 */
const CPD_OUTCOMES: Record<string, { label: string; tone: string; note: string }> = {
  issued:       { label: "Issued",        tone: "bg-emerald-50 text-emerald-700 border-emerald-100", note: "Credited by this run." },
  already_held: { label: "Already held",  tone: "bg-violet-50 text-violet-700 border-violet-100",    note: "Was already credited for this event." },
  unpaid:       { label: "Fee unpaid",    tone: "bg-amber-50 text-amber-700 border-amber-100",       note: "Registration fee is still outstanding." },
  not_a_member: { label: "Not a member",  tone: "bg-gray-100 text-gray-500 border-gray-200",         note: "No portal account, so there is no CPD record to credit." },
  not_issued:   { label: "Not issued",    tone: "bg-rose-50 text-rose-700 border-rose-100",          note: "Eligible but uncredited — check the ledger." },
};

function CpdAllocationResultModal({ result, onClose }: { result: any; onClose: () => void }) {
  const data    = result?.data ?? {};
  const event   = data.event ?? {};
  const summary = data.summary ?? {};
  const rows: any[] = data.rows ?? [];

  const [search, setSearch] = useState("");
  const [outcome, setOutcome] = useState("");

  const q = search.trim().toLowerCase();
  const filtered = rows.filter((r) => {
    if (outcome && r.outcome !== outcome) return false;
    if (!q) return true;
    return [r.name, r.email, r.membership_no, r.registration_no]
      .some((f) => String(f ?? "").toLowerCase().includes(q));
  });

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 px-4 sm:px-6 py-4 border-b sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900">CPD Allocation</h3>
            <p className="text-[11px] text-gray-400 font-medium truncate">
              {event.title}{event.cpd_points != null ? ` · ${event.cpd_points} points per member` : ""}
            </p>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-500 shrink-0" /></button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-bold ${
            summary.issued > 0
              ? "bg-emerald-50 border-emerald-100 text-emerald-700"
              : "bg-gray-50 border-gray-100 text-gray-600"
          }`}>
            <Award className="w-4 h-4 shrink-0" />
            {result?.message}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            <Stat label="Registrations" value={summary.total_registrations ?? 0} />
            <Stat label="Issued now"    value={summary.issued ?? 0}        tone="emerald" />
            <Stat label="Already held"  value={summary.already_held ?? 0}  tone="violet" />
            <Stat label="Fee unpaid"    value={summary.unpaid ?? 0}        tone="amber" />
            <Stat label="Not members"   value={summary.not_a_member ?? 0} />
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, membership no, reg no…"
              className="flex-1 border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-100"
            />
            <select
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm bg-white sm:w-52"
            >
              <option value="">All outcomes</option>
              {Object.entries(CPD_OUTCOMES).map(([key, o]) => (
                <option key={key} value={key}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="border rounded-xl overflow-hidden">
            <div className="max-h-[26rem] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    <th className="text-left px-3 py-2">Delegate</th>
                    <th className="text-left px-3 py-2">Registration</th>
                    <th className="text-right px-3 py-2">CPD</th>
                    <th className="text-left px-3 py-2">Outcome</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filtered.map((r: any) => {
                    const o = CPD_OUTCOMES[r.outcome] ?? CPD_OUTCOMES.not_issued;
                    return (
                      <tr key={r.registration_id}>
                        <td className="px-3 py-2">
                          <div className="font-semibold text-gray-900">{r.name || "—"}</div>
                          <div className="text-[11px] text-gray-400">
                            {[r.membership_no, r.email].filter(Boolean).join(" · ") || "—"}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-[11px] font-mono text-gray-500">{r.registration_no || "—"}</div>
                          <div className="text-[10px] text-gray-400 uppercase font-bold">{r.payment_status}</div>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {r.points != null
                            ? <span className="font-bold text-gray-900">{Number(r.points)}</span>
                            : <span className="text-gray-300">—</span>}
                          {r.issued_at && (
                            <div className="text-[10px] text-gray-400">{r.issued_at.slice(0, 10)}</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            title={o.note}
                            className={`inline-block text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-lg border ${o.tone}`}
                          >
                            {o.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {filtered.length === 0 && (
            <p className="text-sm text-gray-400 font-medium text-center py-4">
              {rows.length === 0 ? "This event has no registrations yet." : "No rows match that filter."}
            </p>
          )}

          <p className="text-[11px] text-gray-400 font-medium">
            CPD goes to registered members whose fee is settled. Attendance is not consulted —
            being on the register is enough. Pressing Issue CPD again is safe: nobody is credited twice.
          </p>
        </div>

        <div className="px-4 sm:px-6 pb-6 pt-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="w-full py-3 border rounded-lg text-sm hover:bg-gray-50">Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
function AdminEventDetailPageInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: event, isLoading, isError } = useAdminGetEventQuery(id!);
  const [complete, { isLoading: completing }]     = useAdminCompleteEventMutation();
  const [allocateCpd, { isLoading: allocating }]  = useAdminAllocateCpdMutation();

  const [tab, setTab] = useState<Tab>("registrations");
  const [msg, setMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  // The register returned by the last CPD run, shown as a sheet.
  const [cpdResult, setCpdResult] = useState<any | null>(null);

  const days: any[] = event?.days ?? [];

  const handleComplete = async () => {
    try {
      await complete(id!).unwrap();
      setMsg({ type: "success", text: "Event marked as completed." });
    } catch (err: any) { setMsg({ type: "error", text: extractError(err) }); }
  };

  /**
   * Issue the event's CPD. Safe to press more than once — the server issues
   * nothing to anyone who already holds this event's CPD — so the button is
   * never disabled after a run and no confirmation stands in the way.
   *
   * The result opens as a register rather than collapsing to a one-line banner:
   * a bare "12 issued" leaves the secretariat with no way to answer the member
   * who rings up asking why they were not credited.
   */
  const handleAllocateCpd = async () => {
    try {
      const res: any = await allocateCpd(id!).unwrap();
      setCpdResult(res);
    } catch (err: any) { setMsg({ type: "error", text: extractError(err) }); }
  };

  if (isLoading) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 text-indigo-500 animate-spin" /></div>;
  if (isError || !event) return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-rose-50 border border-rose-100 rounded-3xl p-8 flex items-center gap-4">
        <AlertCircle className="w-6 h-6 text-rose-500 shrink-0" />
        <p className="text-rose-700 font-semibold">Event not found.</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6 max-w-5xl mx-auto pb-20">

      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-gray-800 transition-colors">
        <ChevronLeft className="w-4 h-4" /> Back to Events
      </button>

      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-2xl sm:rounded-3xl p-5 sm:p-8 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap gap-2 mb-3">
              <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-xl border ${STATUS_COLORS[event.status] ?? "bg-gray-50 text-gray-500 border-gray-100"}`}>
                {event.status}
              </span>
              {event.is_cpd && (
                <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-xl bg-violet-50 text-violet-700 border border-violet-100">
                  CPD +{event.cpd_points}
                </span>
              )}
              {event.is_paid && (
                <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-xl bg-amber-50 text-amber-700 border border-amber-100">
                  ₦{Number(event.price).toLocaleString()}
                </span>
              )}
            </div>
            <h1 className="text-lg sm:text-xl font-black text-gray-900 mb-1 break-words">{event.title}</h1>
            {event.theme && <p className="italic text-gray-400 text-sm mb-3">"{event.theme}"</p>}
            <div className="text-xs sm:text-sm text-gray-500 font-medium">
              {event.start_date ? fmt(event.start_date) : ""}
              {event.end_date && event.end_date !== event.start_date ? ` — ${fmt(event.end_date)}` : ""}
              {event.location ? ` · ${event.location}` : ""}
            </div>
          </div>

          {/* Side by side on a phone, stacked in the header's right column from
              `md` up — three stacked full-width buttons would push the tabs
              below the fold. */}
          <div className="flex flex-row md:flex-col gap-2 flex-shrink-0">
            <AccessGate perm={PERMS.event.scannerUse}>
              <Link
                to={`/admin/events/${id}/scanner`}
                className="flex-1 md:flex-none flex items-center justify-center md:justify-start gap-2 bg-indigo-600 text-white font-black text-xs px-4 py-3 md:py-2.5 rounded-xl hover:bg-indigo-700 transition-colors"
              >
                <QrCode className="w-3.5 h-3.5" /> Scanner
              </Link>
            </AccessGate>
            {/* Completing an event and allocating CPD both change the record
                members are held to — same lifecycle capability. */}
            <AccessGate perm={PERMS.event.lifecycle}>
              {event.status === "published" && (
                <button
                  onClick={handleComplete}
                  disabled={completing}
                  className="flex-1 md:flex-none flex items-center justify-center md:justify-start gap-2 bg-gray-800 text-white font-black text-xs px-4 py-3 md:py-2.5 rounded-xl hover:bg-gray-900 transition-colors disabled:opacity-50"
                >
                  {completing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />}
                  Mark Complete
                </button>
              )}
              {/* Shown from the moment the event is live, not only once it is
                  marked complete: attendance is scanned on the day, and a
                  multi-day event's first-day delegates should not wait on the
                  secretariat remembering to close the event weeks later. It
                  stays available afterwards for the stragglers — pressing it
                  again issues nothing to anyone who already holds the CPD. */}
              {event.is_cpd && (event.status === "published" || event.status === "completed") && (
                <button
                  onClick={handleAllocateCpd}
                  disabled={allocating}
                  title="Issues CPD to registered members whose fee is settled — attendance is not consulted. Safe to press more than once; nobody is credited twice."
                  className="flex-1 md:flex-none flex items-center justify-center md:justify-start gap-2 bg-violet-600 text-white font-black text-xs px-4 py-3 md:py-2.5 rounded-xl hover:bg-violet-700 transition-colors disabled:opacity-50"
                >
                  {allocating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Award className="w-3.5 h-3.5" />}
                  Issue CPD
                </button>
              )}
            </AccessGate>
          </div>
        </div>
      </div>

      {cpdResult && <CpdAllocationResultModal result={cpdResult} onClose={() => setCpdResult(null)} />}

      {/* Feedback message */}
      {msg && (
        <div className={`flex items-center gap-3 px-5 py-4 rounded-2xl border text-sm font-bold ${msg.type === "success" ? "bg-emerald-50 border-emerald-100 text-emerald-700" : "bg-rose-50 border-rose-100 text-rose-700"}`}>
          {msg.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
          {msg.text}
          <button onClick={() => setMsg(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Days quick-view */}
      {days.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {days.map((d: any) => (
            <div key={d.id} className="text-xs font-bold px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-xl">
              {d.day_label} · {fmt(d.day_date)}
            </div>
          ))}
        </div>
      )}

      {/* Tabs. "Registrations" and "Refreshments" alone are wider than a phone
          in five equal columns, so the strip scrolls sideways there and only
          divides the width evenly once there is width to divide. */}
      <div className="bg-white border border-gray-100 rounded-2xl sm:rounded-3xl shadow-sm overflow-hidden">
        <div className="flex border-b border-gray-50 overflow-x-auto no-scrollbar">
          {[
            { key: "registrations" as Tab, label: "Registrations", icon: Users   },
            { key: "documents"     as Tab, label: "Documents",     icon: Paperclip },
            { key: "materials"     as Tab, label: "Materials",     icon: Package },
            { key: "refreshments"  as Tab, label: "Refreshments",  icon: Coffee  },
            { key: "report"        as Tab, label: "Report",        icon: FileText },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 sm:px-2 py-4 text-[10px] sm:text-xs font-black uppercase tracking-wider sm:tracking-widest whitespace-nowrap transition-colors ${
                tab === key
                  ? "border-b-2 border-indigo-500 text-indigo-600"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" /> {label}
            </button>
          ))}
        </div>

        <div className="p-4 sm:p-6">
          {tab === "registrations" && <RegistrationsPanel eventId={id!} days={days} />}
          {tab === "documents"     && <DocumentsPanel eventId={id!} />}
          {tab === "materials"     && <MaterialsPanel eventId={id!} />}
          {tab === "refreshments"  && <RefreshmentsPanel eventId={id!} days={days} />}
          {tab === "report"        && <ReportPanel eventId={id!} />}
        </div>
      </div>
    </div>
  );
}

/**
 * Gated on the events-section read capability.
 */
export default function AdminEventDetailPage() {
  return (
    <PageGate perm={PERMS.event.access}>
      <AdminEventDetailPageInner />
    </PageGate>
  );
}
